import { wibToday } from "@/lib/date";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, rupiah, formatApiError, STATUS_LABELS, fileUrl, slugify } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Seo } from "@/components/Seo";
import { shareScheduleToWA } from "@/lib/shareSchedule";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ShieldCheck, Zap, Star, Plus, Pencil, Trash2, Download, FileSpreadsheet, Megaphone, ChevronLeft, ChevronRight, MessageCircle, Check, Wallet, Share2, Copy, Upload, Globe, Link2, AlertTriangle } from "lucide-react";
import { PhotoUpload } from "@/components/PhotoUpload";

const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

const ACT_LABEL = { customer: "Penumpang", driver: "Driver", admin: "Admin" };
const AUDIT_LABEL = {
  buat_staf: "Membuat akun staf",
  hapus_staf: "Menghapus akun staf",
  reset_sandi_staf: "Mereset kata sandi staf",
  aktif_staf: "Mengaktifkan akun staf",
  nonaktif_staf: "Menonaktifkan akun staf",
  hapus_driver: "Menghapus driver",
  arsip_driver: "Mengarsipkan driver",
  pulih_driver: "Memulihkan driver",
  verifikasi_driver: "Memverifikasi driver",
  aktif_driver: "Mengaktifkan driver",
  nonaktif_driver: "Menonaktifkan driver",
  ubah_status_order: "Mengubah status order",
  daftar_driver: "Mendaftarkan driver",
  hapus_jadwal_massal: "Menghapus jadwal massal",
  ganti_nomor_driver: "Mengganti nomor driver",
  reset_sandi_driver: "Mereset kata sandi driver",
  bersih_duplikat_jadwal: "Membersihkan jadwal duplikat",
};
const ACT_COLOR = { customer: "bg-blue-500/15 text-blue-400", driver: "bg-primary/15 text-primary", admin: "bg-purple-500/15 text-purple-400" };

const PACKAGE_STATUS_LABELS = {
  paket_baru: "Paket Baru", dijemput: "Dijemput", dalam_pengiriman: "Dalam Pengiriman",
  terkirim: "Terkirim", dibatalkan: "Dibatalkan",
};
const PACKAGE_STATUS_KEYS = Object.keys(PACKAGE_STATUS_LABELS);

const COMPLAINT_STATUS = {  baru: "Baru", diproses: "Diproses", menunggu_bukti: "Menunggu Bukti",
  menunggu_driver: "Menunggu Driver", diselesaikan: "Diselesaikan",
  ditolak: "Ditolak", diteruskan: "Diteruskan",
};
const COMPLAINT_CAT = {
  driver_terlambat: "Driver Terlambat", driver_tidak_datang: "Driver Tidak Datang",
  armada_tidak_sesuai: "Armada Tidak Sesuai", harga_tidak_sesuai: "Harga Tidak Sesuai",
  perilaku: "Perilaku", barang_tertinggal: "Barang Tertinggal", pembatalan: "Pembatalan",
  refund: "Refund", keselamatan: "Keselamatan", pembayaran: "Pembayaran",
};

const Stat = ({ label, value, accent }) => (
  <div className="border border-border bg-card p-4">
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-1 font-mono-terminal text-2xl font-bold ${accent ? "text-primary" : ""}`}>{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [actFilter, setActFilter] = useState({ type: "all", q: "", from: "", to: "" });
  const [schedules, setSchedules] = useState([]);
  const [bills, setBills] = useState([]);
  const [ads, setAds] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [articles, setArticles] = useState([]);
  const [artDialog, setArtDialog] = useState(false);
  const [artKey, setArtKey] = useState(0);
  const [slugCheck, setSlugCheck] = useState(null);
  const emptyArt = { id: null, title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "Informasi Rute", tags: [], status: "published", author: "Tim Travele" };
  const [artForm, setArtForm] = useState(emptyArt);
  const [wpDialog, setWpDialog] = useState(false);
  const [wpForm, setWpForm] = useState({ site_url: "", count: 10, status: "draft" });
  const [wpResult, setWpResult] = useState(null);
  const [wpLoading, setWpLoading] = useState(false);
  const [xmlDialog, setXmlDialog] = useState(false);
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlStatus, setXmlStatus] = useState("draft");
  const [xmlType, setXmlType] = useState("");
  const [xmlPhase, setXmlPhase] = useState("idle");
  const [xmlProgress, setXmlProgress] = useState(0);
  const [xmlResult, setXmlResult] = useState(null);
  const [seoData, setSeoData] = useState({ rows: [], summary: {}, total: 0 });
  const [seoFilter, setSeoFilter] = useState("");
  const [seoSearch, setSeoSearch] = useState("");
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoRefreshing, setSeoRefreshing] = useState(false);
  const [seoPage, setSeoPage] = useState(1);
  const [cityData, setCityData] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [series, setSeries] = useState([]);
  const [packages, setPackages] = useState([]);
  const [repFrom, setRepFrom] = useState("");
  const [repTo, setRepTo] = useState("");
  const todayStr = wibToday();
  const [orderDate, setOrderDate] = useState(todayStr);
  const [dateBasis, setDateBasis] = useState("date");
  const [showAllDates, setShowAllDates] = useState(false);
  const [settlements, setSettlements] = useState({});
  const [pkgDate, setPkgDate] = useState(todayStr);
  const [pkgShowAll, setPkgShowAll] = useState(false);
  const [pkgFin, setPkgFin] = useState({});
  const [cities, setCities] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffForm, setStaffForm] = useState({ name: "", phone: "", password: "", role: "admin" });
  const [staffPwd, setStaffPwd] = useState(null);
  const [staffPwdVal, setStaffPwdVal] = useState("");
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ actor: "all", action: "all", q: "", from: "", to: "" });
  const [driverFilter, setDriverFilter] = useState("all");
  const [driverRegOpen, setDriverRegOpen] = useState(false);
  const emptyDriverReg = { name: "", phone: "", password: "", business_name: "", city: "", add_schedule: false, vehicle_name: "", capacity: 7, service_type: "door_to_door", origin: "", destination: "", date: "", time: "", price: "" };
  const [driverRegForm, setDriverRegForm] = useState(emptyDriverReg);
  const [bulkCities, setBulkCities] = useState("");
  const [bulkProvince, setBulkProvince] = useState("");
  const emptySched = { driver_id: "", service_type: "door_to_door", origin: "", destination: "", date: "", time: "", vehicle_name: "", capacity: 7, price: "", pickup_area: "", dropoff_area: "", note: "", repeat_days: 0 };
  const emptyCopy = { source_key: "", dest_origin: "", dest_destinations: [], price: "", repeat_days: 0 };
  const [schedDialog, setSchedDialog] = useState(false);
  const [schedEditId, setSchedEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [schedForm, setSchedForm] = useState(emptySched);

  const orderBasisDate = (o) => (dateBasis === "created" ? (o.created_at || "").slice(0, 10) : o.date);
  const dayOrders = showAllDates ? orders : orders.filter((o) => orderBasisDate(o) === orderDate);
  const daySummary = dayOrders.reduce(
    (a, o) => ({ count: a.count + 1, total: a.total + (o.total || 0), commission: a.commission + (o.commission || 0) }),
    { count: 0, total: 0, commission: 0 }
  );
  const shiftDate = (n) => { const d = new Date(orderDate); d.setDate(d.getDate() + n); setShowAllDates(false); setOrderDate(d.toISOString().slice(0, 10)); };
  const driverName = (id) => { const d = drivers.find((x) => x.id === id); return d ? (d.business_name || d.name) : id; };
  const driverDayRecap = Object.values(dayOrders.reduce((acc, o) => {
    const k = o.driver_id;
    if (!acc[k]) acc[k] = { driver_id: k, name: driverName(k), phone: (drivers.find((x) => x.id === k) || {}).phone || "", count: 0, total: 0, commission: 0, earning: 0 };
    acc[k].count += 1; acc[k].total += o.total || 0; acc[k].commission += o.commission || 0; acc[k].earning += (o.total || 0) - (o.commission || 0);
    return acc;
  }, {})).sort((a, b) => b.commission - a.commission);
  const settleSummary = driverDayRecap.reduce((a, d) => {
    if (settlements[d.driver_id]?.settled) a.settled += d.earning; else a.pending += d.earning;
    return a;
  }, { settled: 0, pending: 0 });
  const pkgBasisDate = (p) => (p.created_at || "").slice(0, 10);
  const dayPackages = pkgShowAll ? packages : packages.filter((p) => pkgBasisDate(p) === pkgDate);
  const pkgSummary = dayPackages.reduce((a, p) => {
    const price = p.price || 0, comm = p.commission || 0, sisa = price - comm;
    a.count += 1; a.total += price; a.commission += comm; a.sisa += sisa;
    if (p.settled) a.settled += sisa; else a.pending += sisa;
    return a;
  }, { count: 0, total: 0, commission: 0, sisa: 0, settled: 0, pending: 0 });
  const shiftPkgDate = (n) => { const d = new Date(pkgDate); d.setDate(d.getDate() + n); setPkgShowAll(false); setPkgDate(d.toISOString().slice(0, 10)); };
  const exportDayCsv = () => {
    const q = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const header = ["No Order", "Penumpang", "WhatsApp", "Driver/Travel", "Armada", "Rute", "Tanggal Berangkat", "Waktu", "Pax", "Total", "Komisi", "Setoran Driver", "Status"];
    const lines = [header.map(q).join(",")];
    dayOrders.forEach((o) => lines.push([o.order_number, o.passenger_name, o.passenger_phone, o.driver_name || "-", o.vehicle_name || "-", `${o.origin} - ${o.destination}`, o.date, o.time, o.pax, o.total, o.commission, (o.total || 0) - (o.commission || 0), STATUS_LABELS[o.status] || o.status].map(q).join(",")));
    const totPax = dayOrders.reduce((a, o) => a + (o.pax || 0), 0);
    lines.push("");
    lines.push(["TOTAL", `${daySummary.count} order`, "", "", "", "", "", "", totPax, daySummary.total, daySummary.commission, daySummary.total - daySummary.commission, ""].map(q).join(","));
    lines.push("");
    lines.push(q("REKAP ORDER PER DRIVER" + (showAllDates ? " (Semua Tanggal)" : ` (${orderDate})`)));
    lines.push(["Driver", "Jumlah Order", "Total", "Komisi", "Setoran Driver", "Status Setor"].map(q).join(","));
    driverDayRecap.forEach((d) => lines.push([d.name, d.count, d.total, d.commission, d.earning, showAllDates ? "-" : (settlements[d.driver_id]?.settled ? "Sudah Disetor" : "Belum Disetor")].map(q).join(",")));
    if (!showAllDates) {
      lines.push("");
      lines.push(q("RINGKASAN SETORAN"));
      lines.push(["Sudah Disetor", settleSummary.settled].map(q).join(","));
      lines.push(["Belum Disetor", settleSummary.pending].map(q).join(","));
      lines.push(["Total Setoran", settleSummary.settled + settleSummary.pending].map(q).join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = showAllDates ? "order-semua-tanggal.csv" : `order-${orderDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Rekap CSV diunduh");
  };
  const sendRecapWa = (d) => {
    if (!d.phone) { toast.error("Nomor WhatsApp driver tidak tersedia"); return; }
    const periode = showAllDates ? "Semua Tanggal" : orderDate;
    const msg = [
      `Halo ${d.name},`, "",
      `Berikut rekap order Travele (${periode}):`,
      `Jumlah Order: ${d.count}`,
      `Total: ${rupiah(d.total)}`,
      `Komisi Platform (20%): ${rupiah(d.commission)}`,
      `Setoran untuk Anda: ${rupiah(d.earning)}`,
      "", "Terima kasih telah bermitra dengan Travele.",
    ].join("\n");
    window.open(`https://wa.me/${d.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const toggleSettled = async (d) => {
    const next = !(settlements[d.driver_id]?.settled);
    try {
      await api.post("/admin/settlements", { driver_id: d.driver_id, date: orderDate, settled: next, amount: d.earning });
      setSettlements((p) => ({ ...p, [d.driver_id]: { driver_id: d.driver_id, date: orderDate, settled: next, amount: d.earning } }));
      toast.success(next ? "Ditandai sudah disetor" : "Ditandai belum disetor");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePkgFinance = async (p) => {
    const f = pkgFin[p.id] || {};
    const price = Number(f.price) || 0; const commission = Number(f.commission) || 0;
    try {
      await api.post(`/admin/packages/${p.id}/finance`, { price, commission });
      setPackages((prev) => prev.map((x) => x.id === p.id ? { ...x, price, commission } : x));
      toast.success("Tarif paket disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePkgSettled = async (p) => {
    const next = !p.settled;
    try {
      await api.post(`/admin/packages/${p.id}/finance`, { settled: next });
      setPackages((prev) => prev.map((x) => x.id === p.id ? { ...x, settled: next } : x));
      toast.success(next ? "Paket ditandai sudah disetor" : "Ditandai belum disetor");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportPkgCsv = () => {
    const q = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const header = ["No Kiriman", "Tanggal Order", "Pengirim", "Penerima", "Rute", "Jenis", "Tarif", "Komisi", "Sisa", "Status Kirim", "Status Setor"];
    const lines = [header.map(q).join(",")];
    dayPackages.forEach((p) => lines.push([p.package_number, pkgBasisDate(p), p.sender_name, p.receiver_name, `${p.origin} - ${p.destination}`, p.item_type === "dokumen" ? "Dokumen" : "Barang", p.price || 0, p.commission || 0, (p.price || 0) - (p.commission || 0), PACKAGE_STATUS_LABELS[p.status] || p.status, p.settled ? "Sudah Disetor" : "Belum Disetor"].map(q).join(",")));
    lines.push("");
    lines.push(["TOTAL", "", "", "", "", `${pkgSummary.count} paket`, pkgSummary.total, pkgSummary.commission, pkgSummary.sisa, "", ""].map(q).join(","));
    lines.push("");
    lines.push(q("RINGKASAN SETORAN" + (pkgShowAll ? " (Semua Tanggal)" : ` (${pkgDate})`)));
    lines.push(["Sudah Disetor", pkgSummary.settled].map(q).join(","));
    lines.push(["Belum Disetor", pkgSummary.pending].map(q).join(","));
    lines.push(["Total Sisa", pkgSummary.sisa].map(q).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = pkgShowAll ? "paket-semua-tanggal.csv" : `paket-${pkgDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Rekap paket CSV diunduh");
  };

  useEffect(() => {
    if (!loading && (!user || (user.role !== "admin" && user.role !== "super_admin"))) navigate("/masuk");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (showAllDates) { setSettlements({}); return; }
    api.get(`/admin/settlements?date=${orderDate}`).then((r) => {
      const m = {}; r.data.forEach((s) => { m[s.driver_id] = s; }); setSettlements(m);
    }).catch(() => {});
  }, [orderDate, showAllDates]);

  const load = () => {
    api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/admin/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/admin/bills").then((r) => setBills(r.data)).catch(() => {});
    api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
    api.get("/admin/complaints").then((r) => setComplaints(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/analytics/timeseries?days=14").then((r) => setSeries(r.data)).catch(() => {});
    api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    if (user?.role === "super_admin") api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    if (user?.role === "super_admin") api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
    if (user?.role === "super_admin") loadSeo();
    if (user?.role === "super_admin") loadCities();
  };
  useEffect(() => { if (user && (user.role === "admin" || user.role === "super_admin")) load(); }, [user]);

  const setOrderStatus = async (id, status) => {
    try { await api.post(`/admin/orders/${id}/status`, { status }); toast.success("Status order diperbarui"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const verify = async (id) => {
    setDrivers((ds) => ds.map((d) => (d.id === id ? { ...d, verified: true } : d)));
    try { await api.post(`/admin/drivers/${id}/verify`); toast.success("Driver diverifikasi"); }
    catch (e) { toast.error("Gagal memverifikasi"); api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {}); }
  };
  const toggleActive = async (id) => {
    setDrivers((ds) => ds.map((d) => (d.id === id ? { ...d, active: d.active === false } : d)));
    try { await api.post(`/admin/drivers/${id}/toggle-active`); }
    catch (e) { toast.error("Gagal mengubah status driver"); api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {}); }
  };
  const togglePriority = async (id) => {
    setDrivers((ds) => ds.map((d) => (d.id === id ? { ...d, priority: !d.priority } : d)));
    try { await api.post(`/admin/drivers/${id}/priority`); }
    catch (e) { toast.error("Gagal mengubah prioritas"); api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {}); }
  };
  const [phoneEdit, setPhoneEdit] = useState(null);
  const [phoneVal, setPhoneVal] = useState("");
  const [pwdEdit, setPwdEdit] = useState(null);
  const [pwdVal, setPwdVal] = useState("");
  const saveDriverPwd = async () => {
    if (pwdVal.length < 6) return toast.error("Kata sandi minimal 6 karakter");
    try { await api.put(`/admin/drivers/${pwdEdit.id}/password`, { password: pwdVal }); toast.success("Kata sandi driver diperbarui"); setPwdEdit(null); setPwdVal(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveDriverPhone = async () => {
    if (!phoneVal.trim()) return toast.error("Nomor WhatsApp wajib diisi");
    try { await api.put(`/admin/drivers/${phoneEdit.id}/phone`, { phone: phoneVal }); toast.success("Nomor driver diperbarui"); setPhoneEdit(null); setPhoneVal(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [delDriver, setDelDriver] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const archiveDriver = async (d) => {
    if (d.archived) {
      try { await api.post(`/admin/drivers/${d.id}/archive`); toast.success("Driver dipulihkan"); load(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    } else {
      setArchiveTarget({ id: d.id, name: d.business_name || d.name }); setArchiveReason("");
    }
  };
  const confirmArchive = async () => {
    try { await api.post(`/admin/drivers/${archiveTarget.id}/archive`, { reason: archiveReason }); toast.success("Driver diarsipkan"); setArchiveTarget(null); setArchiveReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteDriver = async () => {
    try { await api.delete(`/admin/drivers/${delDriver.id}`); toast.success("Driver dihapus"); setDelDriver(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSponsor = async (id) => { await api.post(`/admin/schedules/${id}/sponsor`); toast.success("Status sponsor diubah"); load(); };
  const registerDriver = async () => {
    if (!driverRegForm.name || !driverRegForm.phone || !driverRegForm.password) return toast.error("Nama, WhatsApp, dan kata sandi wajib diisi");
    if (driverRegForm.password.length < 6) return toast.error("Kata sandi minimal 6 karakter");
    const payload = { ...driverRegForm };
    if (payload.add_schedule) {
      if (!payload.origin || !payload.destination || !payload.date || !payload.time || !payload.price) return toast.error("Lengkapi rute, tanggal, jam, dan harga jadwal pertama");
      payload.capacity = Number(payload.capacity) || 7;
      payload.price = Number(payload.price);
    }
    try { await api.post("/admin/drivers-register", payload); toast.success(payload.add_schedule ? "Driver & jadwal pertama dibuat" : "Driver berhasil didaftarkan"); setDriverRegOpen(false); setDriverRegForm(emptyDriverReg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const filteredActivity = activity.filter((e) => {
    if (actFilter.type !== "all" && e.actor_type !== actFilter.type) return false;
    if (actFilter.q && !`${e.actor} ${e.action} ${e.detail || ""}`.toLowerCase().includes(actFilter.q.toLowerCase())) return false;
    const d = (e.at || "").slice(0, 10);
    if (actFilter.from && d < actFilter.from) return false;
    if (actFilter.to && d > actFilter.to) return false;
    return true;
  });
  const exportActivityCsv = () => {
    const rows = [["Waktu", "Tipe", "Pelaku", "Aksi", "Detail"]];
    filteredActivity.forEach((e) => rows.push([
      e.at ? new Date(e.at).toLocaleString("id-ID") : "",
      ACT_LABEL[e.actor_type] || e.actor_type, e.actor || "", e.action || "", e.detail || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `aktivitas-${todayStr}.csv`;
    link.click(); URL.revokeObjectURL(url);
  };
  const filteredAudit = audit.filter((a) => {
    if (auditFilter.actor !== "all" && a.actor_role !== auditFilter.actor) return false;
    if (auditFilter.action !== "all" && a.action !== auditFilter.action) return false;
    const d = (a.created_at || "").slice(0, 10);
    if (auditFilter.from && d < auditFilter.from) return false;
    if (auditFilter.to && d > auditFilter.to) return false;
    if (auditFilter.q) {
      const hay = `${a.actor_name} ${AUDIT_LABEL[a.action] || a.action} ${a.target || ""} ${a.detail || ""}`.toLowerCase();
      if (!hay.includes(auditFilter.q.toLowerCase())) return false;
    }
    return true;
  });
  const exportAuditCsv = () => {
    const rows = [["Waktu", "Pelaku", "Peran", "Tindakan", "Target", "Detail"]];
    filteredAudit.forEach((a) => rows.push([
      a.created_at ? new Date(a.created_at).toLocaleString("id-ID") : "",
      a.actor_name || "", a.actor_role === "super_admin" ? "Super Admin" : "Admin",
      AUDIT_LABEL[a.action] || a.action, a.target || "", a.detail || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `riwayat-audit-${todayStr}.csv`;
    link.click(); URL.revokeObjectURL(url);
  };
  const createStaff = async () => {
    if (!staffForm.name || !staffForm.phone || !staffForm.password) { toast.error("Nama, WhatsApp, dan kata sandi wajib diisi"); return; }
    if (staffForm.password.length < 6) { toast.error("Kata sandi minimal 6 karakter"); return; }
    try { await api.post("/admin/staff", staffForm); toast.success("Akun staf berhasil dibuat"); setStaffForm({ name: "", phone: "", password: "", role: "admin" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleStaff = async (id) => { try { await api.post(`/admin/staff/${id}/toggle`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deleteStaff = async (id) => { try { await api.delete(`/admin/staff/${id}`); toast.success("Akun dihapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resetStaffPwd = async () => {
    if (staffPwdVal.length < 6) { toast.error("Kata sandi minimal 6 karakter"); return; }
    try { await api.put(`/admin/staff/${staffPwd.id}/password`, { password: staffPwdVal }); toast.success("Kata sandi diperbarui"); setStaffPwd(null); setStaffPwdVal(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [delSched, setDelSched] = useState(null);
  const [schedSel, setSchedSel] = useState([]);
  const [massDelDialog, setMassDelDialog] = useState(false);
  const [massDelForm, setMassDelForm] = useState({ driver_id: "", origin: "", destination: "", date: "" });
  const toggleSchedSel = (id) => setSchedSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const schedDupKeys = useMemo(() => {
    const c = {};
    for (const s of schedules) {
      const k = `${s.driver_id}|${s.date}|${s.time}|${s.origin}|${s.destination}`;
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [schedules]);
  const RENDER_LIMIT = 300;
  const toggleSelectAllSched = () => setSchedSel((s) => s.length === schedules.length ? [] : schedules.map((x) => x.id));
  const bulkDeleteSelected = async () => {
    if (schedSel.length === 0) return toast.error("Pilih minimal satu jadwal");
    if (!window.confirm(`Hapus ${schedSel.length} jadwal terpilih secara permanen?`)) return;
    try { const r = await api.post("/admin/schedules/bulk-delete", { ids: schedSel }); toast.success(`${r.data.deleted} jadwal dihapus`); setSchedSel([]); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cleanDuplicates = async () => {
    if (!window.confirm("Hapus semua jadwal kembar (driver + tanggal + jam sama), menyisakan satu per grup? Entri sponsor dipertahankan.")) return;
    try { const r = await api.post("/admin/schedules/dedup"); toast.success(r.data.deleted ? `${r.data.deleted} jadwal duplikat dihapus` : "Tidak ada jadwal duplikat"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const massDeleteByRoute = async () => {
    if (!massDelForm.driver_id && !massDelForm.origin && !massDelForm.destination && !massDelForm.date) return toast.error("Isi minimal satu filter (driver/asal/tujuan/tanggal)");
    try { const r = await api.post("/admin/schedules/bulk-delete", massDelForm); toast.success(`${r.data.deleted} jadwal dihapus`); setMassDelDialog(false); setMassDelForm({ driver_id: "", origin: "", destination: "", date: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelSchedule = async () => {
    if (!delSched) return;
    try {
      await api.delete(`/admin/schedules/${delSched.id}`);
      toast.success("Jadwal dihapus");
      setDelSched(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveBulkCities = async () => {
    if (!bulkCities.trim()) return toast.error("Isi minimal satu nama kota");
    try {
      const { data } = await api.post("/admin/cities/bulk", { names: bulkCities, province: bulkProvince });
      toast.success(`${data.added_count} kota ditambahkan${data.skipped_count ? `, ${data.skipped_count} dilewati (sudah ada)` : ""}`);
      setBulkCities(""); setBulkProvince(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runImport = async () => {
    let payload;
    try { payload = JSON.parse(importText); } catch { toast.error("Format JSON tidak valid"); return; }
    setImporting(true);
    try {
      const r = await api.post("/admin/import/bulk", payload);
      setImportResult(r.data);
      toast.success(`${r.data.drivers_created} driver baru, ${r.data.schedules_created} jadwal ditambahkan`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  const onImportFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(String(ev.target.result || ""));
    reader.readAsText(f);
  };
  const runSeedImport = async () => {
    if (!window.confirm("Impor data seed (198 travel + 29.672 jadwal) ke database lingkungan ini? Aman: hanya MENAMBAH, tidak menimpa/menghapus data lama.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/admin/import/seed-remote", {}, { timeout: 180000 });
      toast.success(`Selesai: ${r.data.drivers_inserted} travel + ${r.data.schedules_inserted} jadwal ditambahkan (total ${r.data.total_schedules})`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal impor seed"); }
    finally { setSeeding(false); }
  };
  const [seedRunning, setSeedRunning] = useState(false);
  const runSeedPreview = async () => {
    if (!window.confirm("Impor data jadwal dari preview (36rb jadwal)? Aman: idempoten, jadwal/driver yang sudah ada tidak akan dobel.")) return;
    setSeedRunning(true);
    try {
      await api.post("/admin/import/seed-preview");
      toast.success("Impor data preview dimulai (berjalan di latar belakang)...");
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      for (;;) {
        await sleep(4000);
        const { data } = await api.get("/admin/import/seed-preview/status");
        if (data.done) {
          if (data.error) toast.error("Gagal: " + data.error);
          else toast.success(`Selesai: ${data.imported} jadwal masuk, ${data.skipped} dilewati (dobel), ${data.drivers_created} driver baru`);
          load(); break;
        }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeedRunning(false); }
  };

  const runGenerateTravels = async () => {
    if (!window.confirm("Generate jadwal travel (±500 travel, 10-15 jadwal acak per travel, jam 17-21, rute balik & charter, sebar 6 bulan)? Aman: hanya menambah & tidak dobel bila diulang.")) return;
    setGenerating(true);
    try {
      const r = await api.post("/admin/import/generate-travels", {}, { timeout: 60000 });
      toast.success(r.data.status === "running" ? "Proses sudah berjalan, mohon tunggu..." : "Generate dimulai di latar belakang. Menunggu selesai...");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const s = await api.get("/admin/import/generate-status");
          if (!s.data.running) {
            clearInterval(poll); setGenerating(false);
            toast.success(`Selesai: ${s.data.travels_with_gen} travel, ${s.data.gen_schedules} jadwal.`);
            load();
          } else if (tries > 60) {
            clearInterval(poll); setGenerating(false);
            toast.info("Masih diproses di latar belakang. Muat ulang halaman beberapa saat lagi.");
          }
        } catch (e) { clearInterval(poll); setGenerating(false); }
      }, 5000);
    } catch (e) { setGenerating(false); toast.error(formatApiError(e.response?.data?.detail) || "Gagal generate"); }
  };

  const openEditSchedule = (s) => {
    setSchedEditId(s.id);
    setSchedForm({ driver_id: s.driver_id, service_type: s.service_type, origin: s.origin, destination: s.destination, date: s.date, time: s.time, vehicle_name: s.vehicle_name, capacity: s.capacity, price: s.price, pickup_area: s.pickup_area || "", dropoff_area: s.dropoff_area || "", note: s.note || "", repeat_days: 0 });
    setSchedDialog(true);
  };
  const saveSchedule = async () => {
    if (!schedForm.driver_id) return toast.error("Pilih driver");
    if (!schedForm.origin || !schedForm.destination || !schedForm.date || !schedForm.time || !schedForm.vehicle_name || !schedForm.price) return toast.error("Lengkapi rute, tanggal, jam, armada, dan harga");
    try {
      if (schedEditId) {
        await api.put(`/admin/schedules/${schedEditId}`, { ...schedForm, price: Number(schedForm.price), capacity: Number(schedForm.capacity) || 7 });
        toast.success("Jadwal diperbarui");
      } else {
        const total = (Number(schedForm.repeat_days) || 0) + 1;
        if (total > 60 && !window.confirm(`Anda akan membuat ${total} jadwal keberangkatan sekaligus. Lanjutkan?`)) return;
        await api.post("/admin/schedules", { ...schedForm, price: Number(schedForm.price), capacity: Number(schedForm.capacity) || 7, repeat_days: Number(schedForm.repeat_days) || 0 });
        toast.success("Jadwal driver ditambahkan");
      }
      setSchedDialog(false); setSchedEditId(null); setSchedForm(emptySched); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const charterMultiplier = (name) => {
    const n = (name || "").toLowerCase();
    if (n.includes("elf")) return 12;
    if (n.includes("hiace")) return 10;
    if (n.includes("innova")) return 7;
    if (n.includes("avanza")) return 6;
    return Number(schedForm.capacity) || 0;
  };
  const [charterReg, setCharterReg] = useState("");
  const applyCharter = () => {
    const mult = charterMultiplier(schedForm.vehicle_name);
    const reg = Number(charterReg);
    if (!reg || !mult) return toast.error("Isi harga tiket reguler & pilih armada (Avanza/Innova/Hiace/Elf)");
    setSchedForm((f) => ({ ...f, price: reg * mult, capacity: mult }));
    toast.success(`Tarif charter: ${rupiah(reg)} × ${mult} = ${rupiah(reg * mult)}`);
  };
  const [copyDialog, setCopyDialog] = useState(false);
  const [copyForm, setCopyForm] = useState(emptyCopy);
  const [copyItems, setCopyItems] = useState([]);
  const routeGroups = Object.values(
    schedules.reduce((acc, s) => {
      const key = `${s.origin}|${s.destination}|${s.service_type}`;
      if (!acc[key]) acc[key] = { key, origin: s.origin, destination: s.destination, service_type: s.service_type, label: s.service_type === "charter" ? "Charter" : "Door to Door", count: 0 };
      acc[key].count++;
      return acc;
    }, {})
  ).sort((a, b) => b.count - a.count);
  const onSelectSource = (key) => {
    const [origin, destination, service_type] = key.split("|");
    const rows = schedules
      .filter((s) => s.origin === origin && s.destination === destination && s.service_type === service_type)
      .map((s) => ({ source_id: s.id, time: s.time, label: `${driverName(s.driver_id)} · ${s.date} · ${s.vehicle_name}`, checked: true }));
    setCopyForm((f) => ({ ...f, source_key: key }));
    setCopyItems(rows);
  };
  const submitCopy = async () => {
    if (!copyForm.source_key) return toast.error("Pilih rute sumber");
    const items = copyItems.filter((i) => i.checked).map((i) => ({ source_id: i.source_id, time: i.time }));
    if (!items.length) return toast.error("Pilih minimal satu jadwal untuk disalin");
    if (!copyForm.dest_origin || copyForm.dest_destinations.length === 0) return toast.error("Pilih rute tujuan (asal & minimal satu kota tujuan)");
    if (!copyForm.price) return toast.error("Isi harga tiket");
    const projected = items.length * copyForm.dest_destinations.length * ((Number(copyForm.repeat_days) || 0) + 1);
    if (projected > 100 && !window.confirm(`Perkiraan hingga ${projected} jadwal akan dibuat (${items.length} jam × ${copyForm.dest_destinations.length} rute × ${(Number(copyForm.repeat_days) || 0) + 1} hari). Duplikat otomatis dilewati. Lanjutkan?`)) return;
    try {
      const { data } = await api.post("/admin/schedules/copy", { dest_origin: copyForm.dest_origin, dest_destinations: copyForm.dest_destinations, price: Number(copyForm.price), repeat_days: Number(copyForm.repeat_days) || 0, items });
      toast.success(`${data.created_count} jadwal dibuat di ${data.destinations} rute${data.skipped_count ? `, ${data.skipped_count} dilewati (sudah ada)` : ""}`);
      setCopyDialog(false); setCopyForm(emptyCopy); setCopyItems([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const payBill = async (id) => { await api.post(`/admin/bills/${id}/paid`); toast.success("Tagihan lunas"); load(); };
  const activateAd = async (id) => { try { await api.post(`/admin/ads/${id}/activate`); toast.success("Iklan aktif — jadwal jadi Sponsor"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const stopAd = async (id) => { await api.post(`/admin/ads/${id}/stop`); toast.success("Iklan dihentikan"); load(); };
  const updateComplaint = async (id, status) => { try { await api.post(`/admin/complaints/${id}/status`, { status }); toast.success("Status pengaduan diperbarui"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const updatePackage = async (id, status) => { try { await api.post(`/admin/packages/${id}/status`, { status }); toast.success("Status kiriman diperbarui"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const assignPackage = async (pid, driver_id) => { try { await api.post(`/admin/packages/${pid}/assign`, { driver_id: driver_id === "none" ? "" : driver_id }); toast.success(driver_id === "none" ? "Penugasan dibatalkan" : "Driver ditugaskan membawa paket"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openNewArticle = () => { setArtForm(emptyArt); setArtKey((k) => k + 1); setArtDialog(true); };
  const openEditArticle = async (id) => { const { data } = await api.get(`/admin/articles/${id}`); setArtForm({ ...data, tags: data.tags || [] }); setArtKey((k) => k + 1); setArtDialog(true); };

  useEffect(() => {
    if (!artDialog) { setSlugCheck(null); return; }
    const eff = (artForm.slug || slugify(artForm.title || "")).trim();
    if (!eff) { setSlugCheck(null); return; }
    const t = setTimeout(() => {
      api.get(`/admin/articles/route-slug-check?slug=${encodeURIComponent(eff)}`)
        .then((r) => setSlugCheck(r.data?.money_page ? r.data : null))
        .catch(() => setSlugCheck(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artDialog, artForm.slug, artForm.title]);

  const artSeo = useMemo(() => {
    const text = (artForm.content || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    const words = text ? text.split(" ").length : 0;
    const metaLen = (artForm.excerpt || "").trim().length;
    const titleLen = (artForm.title || "").trim().length;
    const hasCover = !!artForm.cover_image;
    const hasInternalLink = /<a\s[^>]*href=["']\/[^"']/i.test(artForm.content || "");
    const metaOk = metaLen >= 120 && metaLen <= 160;
    const items = [
      { key: "title", label: "Judul jelas", ok: titleLen >= 10, warn: titleLen > 0 && titleLen < 10, hint: "Idealnya ± 40–60 karakter", w: 15 },
      { key: "meta", label: "Meta description (ringkasan)", ok: metaOk, warn: metaLen > 0 && !metaOk, hint: `Idealnya 120–160 karakter (kini ${metaLen})`, w: 20 },
      { key: "content", label: "Panjang konten", ok: words >= 300, warn: words >= 150 && words < 300, hint: `Minimal 300 kata untuk konten mendalam (kini ${words} kata)`, w: 25 },
      { key: "cover", label: "Gambar cover", ok: hasCover, warn: false, hint: "Tambah gambar utama (tampilan blog & Open Graph)", w: 15 },
      { key: "link", label: "Internal link ke halaman lain", ok: hasInternalLink, warn: false, hint: "Tautkan ke Money Page/rute relevan via tombol Link di editor", w: 15 },
      { key: "slug", label: "Slug tidak bentrok Money Page", ok: !slugCheck, warn: false, hint: "Pakai slug informasional (mis. tips-...)", w: 10 },
    ];
    let score = 0;
    for (const it of items) score += it.ok ? it.w : it.warn ? Math.round(it.w / 2) : 0;
    return { score, items };
  }, [artForm.title, artForm.excerpt, artForm.content, artForm.cover_image, slugCheck]);
  const saveArticle = async () => {
    if (!artForm.title || !artForm.content) { toast.error("Judul dan isi artikel wajib diisi"); return; }
    try {
      const payload = { ...artForm };
      if (artForm.id) await api.put(`/admin/articles/${artForm.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Artikel disimpan");
      setArtDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteArticle = async (id) => { await api.delete(`/admin/articles/${id}`); toast.success("Artikel dihapus"); load(); };

  const importWordpress = async () => {
    if (!wpForm.site_url.trim()) { toast.error("Masukkan URL situs WordPress"); return; }
    setWpLoading(true); setWpResult(null);
    try {
      const { data } = await api.post("/admin/articles/import-wordpress", {
        site_url: wpForm.site_url.trim(), count: Number(wpForm.count) || 10, status: wpForm.status,
      });
      setWpResult(data);
      toast.success(`${data.imported} artikel diimpor, ${data.skipped} dilewati`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWpLoading(false); }
  };

  const importXml = async () => {
    if (!xmlFile) { toast.error("Pilih file XML WordPress terlebih dahulu"); return; }
    const uploadId = `wxr_${Date.now()}`;
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(xmlFile.size / chunkSize);
    setXmlPhase("uploading"); setXmlProgress(0); setXmlResult(null);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const blob = xmlFile.slice(i * chunkSize, (i + 1) * chunkSize);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", blob, xmlFile.name);
        await api.post("/admin/articles/import-wxr/upload", fd);
        setXmlProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      await api.post("/admin/articles/import-wxr/start", { upload_id: uploadId, status: xmlStatus, post_type: xmlType.trim() });
      setXmlPhase("processing");
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      for (;;) {
        await sleep(3000);
        const { data } = await api.get("/admin/articles/import-wxr/status");
        setXmlResult(data);
        if (data.done) {
          if (data.error) toast.error("Gagal impor: " + data.error);
          else toast.success(`${data.imported} artikel diimpor, ${data.skipped} dilewati`);
          setXmlPhase("done"); load(); break;
        }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setXmlPhase("idle"); }
  };

  const loadSeo = async (status = seoFilter, search = seoSearch, page = seoPage) => {
    setSeoLoading(true);
    try { const { data } = await api.get(`/admin/seo/routes`, { params: { status, search, page, limit: 100 } }); setSeoData(data); setSeoPage(page); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeoLoading(false); }
  };
  const refreshSeo = async () => {
    setSeoRefreshing(true);
    try { const { data } = await api.post(`/admin/seo/routes/refresh`); toast.success(`SEO diperbarui: ${data.created} baru, ${data.updated} diperbarui · ${data.indexnow_queued || 0} dikirim ke IndexNow`); loadSeo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeoRefreshing(false); }
  };
  const setSeoStatus = async (slug, action) => {
    try { await api.patch(`/admin/seo/routes/${slug}`, { action }); loadSeo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pingIndexNow = async () => {
    try { const { data } = await api.post(`/admin/seo/indexnow-submit`);
      if (data.result?.ok) toast.success(`${data.submitted} URL dikirim ke IndexNow (Bing/Yandex)`);
      else toast.message(`Dikirim: ${data.submitted} URL. Catatan: ${data.result?.reason || data.result?.status || "aktif hanya di domain produksi"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadCities = async () => {
    setCityLoading(true);
    try { const { data } = await api.get(`/admin/cities`); setCityData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCityLoading(false); }
  };
  const toggleCity = async (slug, hidden) => {
    try { await api.patch(`/admin/cities/${slug}`, { hidden }); setCityData((cs) => cs.map((c) => c.slug === slug ? { ...c, hidden } : c)); toast.success(hidden ? "Kota disembunyikan" : "Kota ditampilkan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const hideEmptyCities = async () => {
    const n = cityData.filter((c) => !c.hidden && (c.schedule_count || 0) === 0).length;
    if (n === 0) { toast.info("Tidak ada kota tanpa jadwal yang perlu disembunyikan."); return; }
    if (!window.confirm(`Sembunyikan ${n} kota tanpa jadwal? Semua akan di-NOINDEX & keluar dari sitemap. Bisa ditampilkan lagi kapan saja.`)) return;
    try {
      const { data } = await api.post(`/admin/cities/hide-empty`);
      setCityData((cs) => cs.map((c) => (!c.hidden && (c.schedule_count || 0) === 0) ? { ...c, hidden: true } : c));
      toast.success(`${data.hidden_count} kota disembunyikan.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadReport = async (rtype) => {
    try {
      const p = new URLSearchParams();
      if (repFrom) p.set("from_date", repFrom);
      if (repTo) p.set("to_date", repTo);
      const res = await api.get(`/admin/reports/${rtype}.csv?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `laporan-${rtype}.csv`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (e) { toast.error("Gagal mengunduh laporan"); }
  };

  if (loading || !user) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Dashboard Admin | Travele" noindex />
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h1 className="mb-1 font-display text-2xl font-bold tracking-tight sm:text-3xl">Dashboard {user.role === "super_admin" ? "Super Admin" : "Admin"}</h1>
        <p className="mb-6 text-sm text-muted-foreground">Kelola order, driver, jadwal, dan komisi Travele.</p>

        {summary && (
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Total Order" value={summary.total_orders} />
            <Stat label="Order Hari Ini" value={summary.orders_today} accent />
            <Stat label="Order Selesai" value={summary.orders_done} />
            <Stat label="Order Dibatalkan" value={summary.orders_cancelled} />
            <Stat label="Driver Aktif" value={summary.active_drivers} />
            <Stat label="Menunggu Verifikasi" value={summary.pending_verification} accent />
            <Stat label="Pendapatan Komisi" value={rupiah(summary.commission_income)} accent />
            <Stat label="Tagihan Belum Bayar" value={rupiah(summary.unpaid_bills)} />
            <Stat label="Pengaduan Aktif" value={summary.pending_complaints ?? 0} accent />
            <Stat label="Iklan Aktif" value={summary.active_ads ?? 0} />
          </div>
        )}

        {summary?.revenue && (
          <div className="mb-8 grid gap-3 sm:grid-cols-2" data-testid="revenue-summary">
            {[
              { key: "today", label: "Hari Ini", t: summary.revenue.travel_today, p: summary.revenue.paket_today, tot: summary.revenue.total_today },
              { key: "month", label: "Bulan Ini", t: summary.revenue.travel_month, p: summary.revenue.paket_month, tot: summary.revenue.total_month },
            ].map((r) => (
              <div key={r.key} className="border border-primary/40 bg-primary/5 p-5" data-testid={`revenue-${r.key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Pendapatan Komisi · {r.label}</span>
                  <Wallet className="h-4 w-4 text-primary" />
                </div>
                <div className="mt-2 font-mono-terminal text-3xl font-black text-primary" data-testid={`revenue-total-${r.key}`}>{rupiah(r.tot)}</div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  <span>Travel: <b className="font-mono-terminal text-foreground">{rupiah(r.t)}</b></span>
                  <span>Paket Kilat: <b className="font-mono-terminal text-foreground">{rupiah(r.p)}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}

        {series.length > 0 && (
          <div className="mb-8 grid gap-4 lg:grid-cols-2" data-testid="charts-panel">
            <div className="border border-border bg-card p-5">
              <h3 className="mb-4 font-display text-lg font-bold">Tren Order (14 Hari)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f5a623" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#f5a623" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#f5a623" }} />
                  <Area type="monotone" dataKey="orders" stroke="#f5a623" strokeWidth={2} fill="url(#gOrders)" name="Order" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-border bg-card p-5">
              <h3 className="mb-4 font-display text-lg font-bold">Pendapatan Komisi (14 Hari)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={series} margin={{ top: 5, right: 10, left: 6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#f5a623" }} />
                  <Bar dataKey="commission" fill="#f5a623" radius={[3, 3, 0, 0]} name="Komisi" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {analytics && (
          <div className="mb-8 grid gap-4 lg:grid-cols-3" data-testid="analytics-panel">
            <div className="border border-border bg-card p-5">
              <h3 className="mb-3 font-display text-lg font-bold">Rute Terpopuler</h3>
              {analytics.top_routes.length === 0 && <p className="text-sm text-muted-foreground">Belum ada data.</p>}
              {analytics.top_routes.map((r, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <span>{r.origin} → {r.destination}</span>
                  <span className="font-mono-terminal text-primary">{r.count} order</span>
                </div>
              ))}
            </div>
            <div className="border border-border bg-card p-5">
              <h3 className="mb-3 font-display text-lg font-bold">Kota Permintaan Tertinggi</h3>
              {analytics.top_cities.length === 0 && <p className="text-sm text-muted-foreground">Belum ada data.</p>}
              {analytics.top_cities.map((c, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <span>{c.city}</span>
                  <span className="font-mono-terminal text-primary">{c.count}</span>
                </div>
              ))}
            </div>
            <div className="border border-border bg-card p-5">
              <h3 className="mb-3 font-display text-lg font-bold">Driver Rating Terbaik</h3>
              {analytics.top_drivers.length === 0 && <p className="text-sm text-muted-foreground">Belum ada data.</p>}
              {analytics.top_drivers.map((d, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <span className="truncate">{d.name}</span>
                  <span className="flex items-center gap-1 font-mono-terminal text-primary"><Star className="h-3 w-3 fill-primary" />{d.rating}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Tabs defaultValue="orders">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="orders" data-testid="tab-admin-orders">Order Masuk</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-admin-drivers">Driver & Verifikasi</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-admin-schedules">Jadwal & Sponsor</TabsTrigger>
            <TabsTrigger value="bills" data-testid="tab-admin-bills">Komisi & Tagihan</TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-admin-ads">Iklan Sponsor</TabsTrigger>
            <TabsTrigger value="complaints" data-testid="tab-admin-complaints">Pengaduan</TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-admin-packages">Paket Kilat</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-admin-blog">Blog</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-admin-reports">Laporan</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-admin-activity">Aktivitas</TabsTrigger>
            {user?.role === "super_admin" && <TabsTrigger value="staff" data-testid="tab-admin-staff">Manajemen Admin</TabsTrigger>}
            {user?.role === "super_admin" && <TabsTrigger value="audit" data-testid="tab-admin-audit">Riwayat Audit</TabsTrigger>}
            {user?.role === "super_admin" && <TabsTrigger value="seo" data-testid="tab-admin-seo">SEO Money Pages</TabsTrigger>}
            {user?.role === "super_admin" && <TabsTrigger value="cities" data-testid="tab-admin-cities">Kelola Kota</TabsTrigger>}
          </TabsList>
          <TabsContent value="activity">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="mr-auto text-sm text-muted-foreground">Aktivitas terkini penumpang &amp; driver{user?.role === "super_admin" ? " & admin" : ""}.</p>
              <Button size="sm" variant="secondary" data-testid="activity-export" onClick={exportActivityCsv}><Download className="mr-1 h-4 w-4" />Ekspor CSV</Button>
              <Button size="sm" variant="secondary" data-testid="activity-reload" onClick={load}>Muat ulang</Button>
            </div>
            <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <div className="grid gap-1"><Label className="text-xs">Tipe</Label>
                <Select value={actFilter.type} onValueChange={(v) => setActFilter({ ...actFilter, type: v })}>
                  <SelectTrigger data-testid="activity-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="customer">Penumpang</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    {user?.role === "super_admin" && <SelectItem value="admin">Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1"><Label className="text-xs">Cari nama / aksi</Label><Input data-testid="activity-filter-q" value={actFilter.q} onChange={(e) => setActFilter({ ...actFilter, q: e.target.value })} placeholder="cth. Budi, order, PKT" /></div>
              <div className="grid gap-1"><Label className="text-xs">Dari tanggal</Label><Input data-testid="activity-filter-from" type="date" value={actFilter.from} onChange={(e) => setActFilter({ ...actFilter, from: e.target.value })} /></div>
              <div className="grid gap-1"><Label className="text-xs">Sampai tanggal</Label><Input data-testid="activity-filter-to" type="date" value={actFilter.to} onChange={(e) => setActFilter({ ...actFilter, to: e.target.value })} /></div>
            </div>
            <div className="divide-y divide-border border border-border bg-card" data-testid="activity-feed">
              {(() => {
                const rows = filteredActivity;
                if (rows.length === 0) return <p className="p-6 text-center text-muted-foreground" data-testid="activity-empty">Tidak ada aktivitas sesuai filter.</p>;
                return rows.map((e, i) => (
                  <div key={i} data-testid={`activity-row-${i}`} className="flex items-start gap-3 p-3">
                    <span className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ACT_COLOR[e.actor_type] || "bg-secondary"}`}>{ACT_LABEL[e.actor_type] || e.actor_type}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm"><b>{e.actor}</b> — {e.action}</div>
                      {e.detail && <div className="truncate font-mono-terminal text-xs text-muted-foreground">{e.detail}</div>}
                    </div>
                    <div className="shrink-0 font-mono-terminal text-[10px] text-muted-foreground">{e.at ? new Date(e.at).toLocaleString("id-ID") : ""}</div>
                  </div>
                ));
              })()}
            </div>
          </TabsContent>

          {user?.role === "super_admin" && (
          <TabsContent value="staff">
            <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
              <div className="h-fit border border-border bg-card p-5" data-testid="staff-create-form">
                <h3 className="mb-3 font-display text-lg font-bold">Buat Akun Staf</h3>
                <p className="mb-4 text-xs text-muted-foreground">Buat akun Admin atau Super Admin baru. Login memakai Nomor WhatsApp + Kata Sandi.</p>
                <div className="grid gap-3">
                  <div className="grid gap-1"><Label className="text-xs">Nama</Label><Input data-testid="staff-name" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="Nama lengkap" /></div>
                  <div className="grid gap-1"><Label className="text-xs">Nomor WhatsApp</Label><Input data-testid="staff-phone" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="08xxxxxxxxxx" /></div>
                  <div className="grid gap-1"><Label className="text-xs">Kata Sandi</Label><Input data-testid="staff-password" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} placeholder="Minimal 6 karakter" /></div>
                  <div className="grid gap-1"><Label className="text-xs">Peran</Label>
                    <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                      <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="staff-create-btn" onClick={createStaff} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" /> Buat Akun</Button>
                </div>
              </div>
              <div className="border border-border bg-card p-5">
                <h3 className="mb-3 font-display text-lg font-bold">Daftar Admin & Super Admin</h3>
                <Table data-testid="staff-table">
                  <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>WhatsApp</TableHead><TableHead>Peran</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {staff.map((s) => (
                      <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                        <TableCell className="font-medium">{s.name}{s.id === user.id && <span className="ml-1 text-xs text-primary">(Anda)</span>}</TableCell>
                        <TableCell className="font-mono-terminal text-xs">{s.phone}</TableCell>
                        <TableCell><Badge variant="secondary">{s.role === "super_admin" ? "Super Admin" : "Admin"}</Badge></TableCell>
                        <TableCell><Badge className={s.active !== false ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}>{s.active !== false ? "Aktif" : "Nonaktif"}</Badge></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="secondary" data-testid={`staff-pwd-${s.id}`} onClick={() => { setStaffPwd({ id: s.id, name: s.name }); setStaffPwdVal(""); }}>Sandi</Button>
                            {s.id !== user.id && <Button size="sm" variant="secondary" data-testid={`staff-toggle-${s.id}`} onClick={() => toggleStaff(s.id)}>{s.active !== false ? "Nonaktifkan" : "Aktifkan"}</Button>}
                            {s.id !== user.id && <Button size="sm" variant="destructive" data-testid={`staff-delete-${s.id}`} onClick={() => deleteStaff(s.id)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {staff.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Belum ada data.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
            <Dialog open={!!staffPwd} onOpenChange={(o) => { if (!o) { setStaffPwd(null); setStaffPwdVal(""); } }}>
              <DialogContent data-testid="staff-pwd-dialog">
                <DialogHeader><DialogTitle>Ubah Kata Sandi — {staffPwd?.name}</DialogTitle></DialogHeader>
                <div className="grid gap-1"><Label className="text-xs">Kata Sandi Baru</Label><Input data-testid="staff-pwd-input" value={staffPwdVal} onChange={(e) => setStaffPwdVal(e.target.value)} placeholder="Minimal 6 karakter" /></div>
                <DialogFooter><Button data-testid="staff-pwd-save" onClick={resetStaffPwd} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
          )}

          {user?.role === "super_admin" && (
          <TabsContent value="audit">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="mr-auto text-sm text-muted-foreground">Catatan tindakan penting oleh Admin &amp; Super Admin — akun staf, verifikasi/arsip/hapus driver, dan perubahan status order.</p>
              <Button size="sm" variant="secondary" data-testid="audit-export" onClick={exportAuditCsv}><Download className="mr-1 h-4 w-4" />Ekspor CSV</Button>
              <Button size="sm" variant="secondary" data-testid="audit-reload" onClick={load}>Muat ulang</Button>
            </div>
            <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              <div className="grid gap-1"><Label className="text-xs">Pelaku</Label>
                <Select value={auditFilter.actor} onValueChange={(v) => setAuditFilter({ ...auditFilter, actor: v })}>
                  <SelectTrigger data-testid="audit-filter-actor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1"><Label className="text-xs">Jenis Tindakan</Label>
                <Select value={auditFilter.action} onValueChange={(v) => setAuditFilter({ ...auditFilter, action: v })}>
                  <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    {Object.keys(AUDIT_LABEL).map((k) => <SelectItem key={k} value={k}>{AUDIT_LABEL[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1"><Label className="text-xs">Cari</Label><Input data-testid="audit-filter-q" value={auditFilter.q} onChange={(e) => setAuditFilter({ ...auditFilter, q: e.target.value })} placeholder="Nama / target / detail" /></div>
              <div className="grid gap-1"><Label className="text-xs">Dari tanggal</Label><Input data-testid="audit-filter-from" type="date" value={auditFilter.from} onChange={(e) => setAuditFilter({ ...auditFilter, from: e.target.value })} /></div>
              <div className="grid gap-1"><Label className="text-xs">Sampai tanggal</Label><Input data-testid="audit-filter-to" type="date" value={auditFilter.to} onChange={(e) => setAuditFilter({ ...auditFilter, to: e.target.value })} /></div>
            </div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground" data-testid="audit-count">{filteredAudit.length} catatan</span>
              {(auditFilter.actor !== "all" || auditFilter.action !== "all" || auditFilter.q || auditFilter.from || auditFilter.to) && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid="audit-clear" onClick={() => setAuditFilter({ actor: "all", action: "all", q: "", from: "", to: "" })}>Reset filter</Button>
              )}
            </div>
            <div className="border border-border bg-card" data-testid="audit-table">
              <Table>
                <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Pelaku</TableHead><TableHead>Tindakan</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredAudit.map((a) => (
                    <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                      <TableCell className="whitespace-nowrap font-mono-terminal text-xs">{a.created_at ? new Date(a.created_at).toLocaleString("id-ID") : ""}</TableCell>
                      <TableCell><b>{a.actor_name || "-"}</b> <span className="text-xs text-muted-foreground">({a.actor_role === "super_admin" ? "Super Admin" : "Admin"})</span></TableCell>
                      <TableCell><Badge variant="secondary">{AUDIT_LABEL[a.action] || a.action}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.target}{a.detail ? ` — ${a.detail}` : ""}</TableCell>
                    </TableRow>
                  ))}
                  {filteredAudit.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground" data-testid="audit-empty">Tidak ada catatan sesuai filter.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          )}

          {user?.role === "super_admin" && (
          <TabsContent value="seo">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="mr-auto max-w-xl text-sm text-muted-foreground">Kelola indeks Google per halaman rute (Money Page). Quality Gate menilai kelayakan otomatis (skor 0-100); Anda bisa override manual INDEX/NOINDEX. Hanya URL <b>INDEX</b> yang masuk sitemap. Perubahan status otomatis dikirim ke IndexNow (Bing/Yandex).</p>
              <Button data-testid="seo-indexnow-btn" variant="outline" onClick={pingIndexNow} className="border-sky-500/40 font-bold text-sky-400 hover:bg-sky-500/10">Submit ke IndexNow</Button>
              <Button data-testid="seo-refresh-btn" onClick={refreshSeo} disabled={seoRefreshing} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">{seoRefreshing ? "Memproses..." : "Refresh dari Data"}</Button>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="seo-summary">
              {["ALL", "INDEX", "NOINDEX"].map((k) => {
                const active = seoFilter === (k === "ALL" ? "" : k);
                return (
                  <button key={k} data-testid={`seo-filter-${k}`} onClick={() => { const f = k === "ALL" ? "" : k; setSeoFilter(f); loadSeo(f, seoSearch, 1); }} className={`border px-3 py-1.5 text-sm transition-colors ${active ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                    {k === "ALL" ? "Semua" : k} <span className="ml-1 font-mono-terminal font-bold">{seoData.summary?.[k] || 0}</span>
                  </button>
                );
              })}
              <Input data-testid="seo-search" value={seoSearch} onChange={(e) => setSeoSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") loadSeo(seoFilter, seoSearch, 1); }} placeholder="Cari kota / slug lalu Enter..." className="ml-auto max-w-xs" />
            </div>
            <div className="overflow-x-auto border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Rute</TableHead><TableHead>Jadwal</TableHead><TableHead>Mitra</TableHead>
                  <TableHead>Skor</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {seoData.rows.map((r) => (
                    <TableRow key={r.slug} data-testid={`seo-row-${r.slug}`}>
                      <TableCell><a href={`/${r.slug}`} target="_blank" rel="noreferrer" className="font-semibold hover:text-primary hover:underline">{r.origin} → {r.destination}</a><div className="font-mono-terminal text-[10px] text-muted-foreground">{r.slug}</div></TableCell>
                      <TableCell className="font-mono-terminal">{r.schedule_count}</TableCell>
                      <TableCell className="font-mono-terminal">{r.operator_count}</TableCell>
                      <TableCell><span title={(r.quality_reasons || []).join(", ") || "Lolos semua kriteria"} className={`font-mono-terminal font-bold ${r.quality_score >= 70 ? "text-emerald-400" : r.quality_score >= 50 ? "text-amber-400" : "text-red-400"}`}>{r.quality_score}</span></TableCell>
                      <TableCell><Badge className={r.seo_status === "INDEX" ? "bg-emerald-500/15 text-emerald-400" : "bg-muted text-muted-foreground"}>{r.seo_status}{r.manual_override ? " ✋" : ""}</Badge>{r.indexnow_at && <div className="mt-0.5 font-mono-terminal text-[9px] text-muted-foreground" title="Terakhir disubmit ke IndexNow">IndexNow: {new Date(r.indexnow_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}</div>}</TableCell>
                      <TableCell className="space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" data-testid={`seo-index-${r.slug}`} onClick={() => setSeoStatus(r.slug, "index")}>INDEX</Button>
                        <Button size="sm" variant="outline" data-testid={`seo-noindex-${r.slug}`} onClick={() => setSeoStatus(r.slug, "noindex")}>NOINDEX</Button>
                        <Button size="sm" variant="ghost" data-testid={`seo-auto-${r.slug}`} onClick={() => setSeoStatus(r.slug, "auto")}>Auto</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {seoData.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{seoLoading ? "Memuat..." : 'Belum ada data. Klik "Refresh dari Data" untuk membangun daftar Money Page dari jadwal aktif.'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            {seoData.total > 0 && (
              <div className="mt-3 flex items-center justify-between text-sm" data-testid="seo-pagination">
                <span className="text-muted-foreground">Menampilkan {(seoPage - 1) * 100 + 1}–{Math.min(seoPage * 100, seoData.total)} dari {seoData.total} rute</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="seo-prev" disabled={seoPage <= 1 || seoLoading} onClick={() => loadSeo(seoFilter, seoSearch, seoPage - 1)}>Sebelumnya</Button>
                  <Button size="sm" variant="outline" data-testid="seo-next" disabled={seoPage * 100 >= seoData.total || seoLoading} onClick={() => loadSeo(seoFilter, seoSearch, seoPage + 1)}>Berikutnya</Button>
                </div>
              </div>
            )}
          </TabsContent>
          )}

          {user?.role === "super_admin" && (
          <TabsContent value="cities">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="mr-auto max-w-xl text-sm text-muted-foreground">Sembunyikan kota tanpa layanan agar tak jadi halaman tipis (otomatis NOINDEX + keluar dari sitemap & dropdown pencarian). Bisa ditampilkan lagi kapan saja.</p>
              <Button size="sm" variant="destructive" data-testid="cities-hide-empty-btn" onClick={hideEmptyCities}>
                Sembunyikan Semua Kota Tanpa Jadwal ({cityData.filter((c) => !c.hidden && (c.schedule_count || 0) === 0).length})
              </Button>
              <Input data-testid="city-search" value={citySearch} onChange={(e) => setCitySearch(e.target.value)} placeholder="Cari kota..." className="max-w-xs" />
            </div>
            <div className="overflow-x-auto border border-border">
              <Table>
                <TableHeader><TableRow><TableHead>Kota</TableHead><TableHead>Jadwal</TableHead><TableHead>Status SEO</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
                <TableBody>
                  {cityData.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 300).map((c) => (
                    <TableRow key={c.slug} data-testid={`city-row-${c.slug}`}>
                      <TableCell className="font-semibold">{c.name}</TableCell>
                      <TableCell className="font-mono-terminal">{c.schedule_count}</TableCell>
                      <TableCell>{c.hidden ? <Badge variant="secondary" className="text-muted-foreground">Disembunyikan</Badge> : <Badge className="bg-emerald-500/15 text-emerald-400">Tampil</Badge>}</TableCell>
                      <TableCell>{c.hidden
                        ? <Button size="sm" variant="outline" data-testid={`city-show-${c.slug}`} onClick={() => toggleCity(c.slug, false)}>Tampilkan</Button>
                        : <Button size="sm" variant="outline" data-testid={`city-hide-${c.slug}`} onClick={() => toggleCity(c.slug, true)}>Sembunyikan</Button>}</TableCell>
                    </TableRow>
                  ))}
                  {cityData.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{cityLoading ? "Memuat..." : "Belum ada data kota."}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          )}

          <TabsContent value="orders">
            <div className="mb-3 flex flex-wrap items-center gap-2 border border-border bg-card p-3">
              <div className="flex items-center gap-1">
                <Button size="icon" variant="secondary" data-testid="orders-prev-day" onClick={() => shiftDate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Input type="date" data-testid="orders-date" value={orderDate} onChange={(e) => { setShowAllDates(false); setOrderDate(e.target.value); }} className="w-40" disabled={showAllDates} />
                <Button size="icon" variant="secondary" data-testid="orders-next-day" onClick={() => shiftDate(1)}><ChevronRight className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" data-testid="orders-today" onClick={() => { setShowAllDates(false); setOrderDate(todayStr); }}>Hari Ini</Button>
              </div>
              <Select value={dateBasis} onValueChange={setDateBasis}>
                <SelectTrigger className="w-48" data-testid="orders-date-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Tanggal Berangkat</SelectItem>
                  <SelectItem value="created">Tanggal Order</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant={showAllDates ? "default" : "outline"} data-testid="orders-toggle-all" onClick={() => setShowAllDates(!showAllDates)} className={showAllDates ? "bg-primary text-primary-foreground" : ""}>
                {showAllDates ? "Tampilkan Harian" : "Semua Tanggal"}
              </Button>
              <Button size="sm" variant="secondary" data-testid="orders-export-csv" onClick={exportDayCsv} disabled={dayOrders.length === 0}>
                <Download className="mr-1 h-4 w-4" /> Ekspor Harian (CSV)
              </Button>
              <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
                <span>Order: <b data-testid="day-order-count">{daySummary.count}</b></span>
                <span>Total: <b className="font-mono-terminal">{rupiah(daySummary.total)}</b></span>
                <span>Komisi: <b className="font-mono-terminal text-primary" data-testid="day-order-commission">{rupiah(daySummary.commission)}</b></span>
              </div>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>No. Order</TableHead><TableHead>Penumpang</TableHead><TableHead>Driver/Travel</TableHead><TableHead>Rute</TableHead>
                  <TableHead>Total</TableHead><TableHead>Komisi</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dayOrders.map((o) => (
                    <TableRow key={o.id} data-testid={`admin-order-${o.order_number}`}>
                      <TableCell className="font-mono-terminal">{o.order_number}</TableCell>
                      <TableCell><div>{o.passenger_name}</div><div className="text-xs text-muted-foreground">{o.passenger_phone}</div></TableCell>
                      <TableCell data-testid={`admin-order-driver-${o.order_number}`}>
                        {o.driver_id ? (
                          <a href={`/driver/${o.driver_id}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">{o.driver_name || "Driver"}</a>
                        ) : (
                          <span className="font-semibold">{o.driver_name || "-"}</span>
                        )}
                        <div className="text-xs text-muted-foreground">{o.vehicle_name}</div>
                      </TableCell>
                      <TableCell>{o.origin} → {o.destination}<div className="text-xs text-muted-foreground">{o.date} {o.time} · {o.pax} pax</div></TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(o.total)}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(o.commission)}</TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={(v) => setOrderStatus(o.order_number, v)}>
                          <SelectTrigger data-testid={`order-status-${o.order_number}`} className="w-52"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {dayOrders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">{showAllDates ? "Belum ada order." : "Tidak ada order pada tanggal ini."}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 border border-border" data-testid="driver-recap">
              <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2 text-sm font-semibold">
                <span>Rekap Order per Driver{showAllDates ? " (Semua Tanggal)" : ` — ${orderDate}`}</span>
                <span className="text-xs font-normal text-muted-foreground">Setoran = Total − Komisi (20%)</span>
              </div>
              {!showAllDates && (
                <div className="flex flex-wrap gap-x-6 gap-y-1 border-b border-border bg-background px-4 py-2 text-sm">
                  <span>Sudah disetor: <b className="font-mono-terminal text-emerald-400" data-testid="settle-summary-done">{rupiah(settleSummary.settled)}</b></span>
                  <span>Belum disetor: <b className="font-mono-terminal text-amber-400" data-testid="settle-summary-pending">{rupiah(settleSummary.pending)}</b></span>
                  <span className="text-muted-foreground">Total setoran: <b className="font-mono-terminal text-foreground">{rupiah(settleSummary.settled + settleSummary.pending)}</b></span>
                </div>
              )}
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Driver</TableHead><TableHead>Jumlah Order</TableHead><TableHead>Total</TableHead><TableHead>Komisi</TableHead><TableHead>Setoran Driver</TableHead><TableHead>Status Setor</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {driverDayRecap.map((d) => (
                    <TableRow key={d.driver_id} data-testid={`driver-recap-${d.driver_id}`}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell data-testid={`driver-recap-count-${d.driver_id}`}>{d.count}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(d.total)}</TableCell>
                      <TableCell className="font-mono-terminal text-primary" data-testid={`driver-recap-commission-${d.driver_id}`}>{rupiah(d.commission)}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(d.earning)}</TableCell>
                      <TableCell data-testid={`driver-recap-status-${d.driver_id}`}>
                        {showAllDates ? (
                          <span className="text-xs text-muted-foreground">Pilih tanggal</span>
                        ) : settlements[d.driver_id]?.settled ? (
                          <Badge className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/15"><Check className="mr-1 h-3 w-3" /> Sudah Disetor</Badge>
                        ) : (
                          <Badge variant="secondary">Belum Disetor</Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="secondary" data-testid={`driver-recap-wa-${d.driver_id}`} onClick={() => sendRecapWa(d)} className="bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25">
                          <MessageCircle className="mr-1 h-4 w-4" /> WA
                        </Button>
                        {!showAllDates && (
                          <Button size="sm" variant={settlements[d.driver_id]?.settled ? "outline" : "default"} data-testid={`driver-recap-settle-${d.driver_id}`} onClick={() => toggleSettled(d)} className={settlements[d.driver_id]?.settled ? "" : "bg-emerald-500 text-white hover:bg-emerald-600"}>
                            {settlements[d.driver_id]?.settled ? "Batalkan" : "Tandai Disetor"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {driverDayRecap.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Tidak ada data pada tanggal ini.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="drivers">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Filter Status</Label>
              <div className="w-52">
                <Select value={driverFilter} onValueChange={setDriverFilter}>
                  <SelectTrigger data-testid="driver-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Driver</SelectItem>
                    <SelectItem value="verified">Terverifikasi</SelectItem>
                    <SelectItem value="pending">Menunggu Verifikasi</SelectItem>
                    <SelectItem value="archived">Diarsipkan / Diblokir</SelectItem>
                    <SelectItem value="inactive">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const c = drivers.filter((d) => (
                  driverFilter === "all" ? true :
                  driverFilter === "verified" ? d.verified && !d.archived :
                  driverFilter === "pending" ? !d.verified && !d.archived :
                  driverFilter === "archived" ? d.archived :
                  driverFilter === "inactive" ? (!d.active && !d.archived) : true
                )).length;
                return <span className="text-xs text-muted-foreground" data-testid="driver-filter-count">{c} driver</span>;
              })()}
              <Button size="sm" data-testid="driver-register-btn" onClick={() => setDriverRegOpen(true)} className="ml-auto bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" />Daftar Driver</Button>
            </div>
            <Dialog open={driverRegOpen} onOpenChange={(o) => { setDriverRegOpen(o); if (!o) setDriverRegForm(emptyDriverReg); }}>
              <DialogContent data-testid="driver-register-dialog">
                <DialogHeader><DialogTitle>Daftarkan Driver Baru</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Driver dibuat langsung terverifikasi. Login memakai Nomor WhatsApp + Kata Sandi.</p>
                <div className="grid gap-3">
                  <div className="grid gap-1"><Label className="text-xs">Nama Driver</Label><Input data-testid="driver-reg-name" value={driverRegForm.name} onChange={(e) => setDriverRegForm({ ...driverRegForm, name: e.target.value })} placeholder="Nama lengkap" /></div>
                  <div className="grid gap-1"><Label className="text-xs">Nama Usaha / Travel</Label><Input data-testid="driver-reg-business" value={driverRegForm.business_name} onChange={(e) => setDriverRegForm({ ...driverRegForm, business_name: e.target.value })} placeholder="cth. Travel Berkah Jaya (opsional)" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1"><Label className="text-xs">Nomor WhatsApp</Label><Input data-testid="driver-reg-phone" value={driverRegForm.phone} onChange={(e) => setDriverRegForm({ ...driverRegForm, phone: e.target.value })} placeholder="08xxxxxxxxxx" /></div>
                    <div className="grid gap-1"><Label className="text-xs">Kota</Label><Input data-testid="driver-reg-city" value={driverRegForm.city} onChange={(e) => setDriverRegForm({ ...driverRegForm, city: e.target.value })} placeholder="cth. Semarang" /></div>
                  </div>
                  <div className="grid gap-1"><Label className="text-xs">Kata Sandi</Label><Input data-testid="driver-reg-password" value={driverRegForm.password} onChange={(e) => setDriverRegForm({ ...driverRegForm, password: e.target.value })} placeholder="Minimal 6 karakter" /></div>
                  <label className="flex items-center gap-2 text-sm" data-testid="driver-reg-addsched-toggle">
                    <input type="checkbox" checked={driverRegForm.add_schedule} onChange={(e) => setDriverRegForm({ ...driverRegForm, add_schedule: e.target.checked })} className="h-4 w-4 accent-primary" />
                    Sekaligus tambah armada &amp; jadwal pertama (langsung tampil di terminal)
                  </label>
                  {driverRegForm.add_schedule && (
                    <div className="grid gap-3 border-t border-border pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1"><Label className="text-xs">Armada</Label><Input data-testid="driver-reg-vehicle" value={driverRegForm.vehicle_name} onChange={(e) => setDriverRegForm({ ...driverRegForm, vehicle_name: e.target.value })} placeholder="cth. Toyota Hiace" /></div>
                        <div className="grid gap-1"><Label className="text-xs">Kapasitas</Label><Input data-testid="driver-reg-capacity" type="number" value={driverRegForm.capacity} onChange={(e) => setDriverRegForm({ ...driverRegForm, capacity: e.target.value })} /></div>
                      </div>
                      <div className="grid gap-1"><Label className="text-xs">Layanan</Label>
                        <Select value={driverRegForm.service_type} onValueChange={(v) => setDriverRegForm({ ...driverRegForm, service_type: v })}>
                          <SelectTrigger data-testid="driver-reg-service"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="door_to_door">Travel Door to Door</SelectItem><SelectItem value="charter">Charter Drop Off</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1"><Label className="text-xs">Asal</Label><Input data-testid="driver-reg-origin" value={driverRegForm.origin} onChange={(e) => setDriverRegForm({ ...driverRegForm, origin: e.target.value })} placeholder="cth. Semarang" /></div>
                        <div className="grid gap-1"><Label className="text-xs">Tujuan</Label><Input data-testid="driver-reg-destination" value={driverRegForm.destination} onChange={(e) => setDriverRegForm({ ...driverRegForm, destination: e.target.value })} placeholder="cth. Jakarta" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="grid gap-1"><Label className="text-xs">Tanggal</Label><Input data-testid="driver-reg-date" type="date" value={driverRegForm.date} onChange={(e) => setDriverRegForm({ ...driverRegForm, date: e.target.value })} /></div>
                        <div className="grid gap-1"><Label className="text-xs">Jam</Label><Input data-testid="driver-reg-time" type="time" value={driverRegForm.time} onChange={(e) => setDriverRegForm({ ...driverRegForm, time: e.target.value })} /></div>
                        <div className="grid gap-1"><Label className="text-xs">Harga</Label><Input data-testid="driver-reg-price" type="number" value={driverRegForm.price} onChange={(e) => setDriverRegForm({ ...driverRegForm, price: e.target.value })} placeholder="300000" /></div>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter><Button data-testid="driver-reg-submit" onClick={registerDriver} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" />Daftarkan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Usaha</TableHead><TableHead>WhatsApp</TableHead><TableHead>Kota</TableHead>
                  <TableHead>Jadwal</TableHead><TableHead>Rating</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {drivers.filter((d) => (
                    driverFilter === "all" ? true :
                    driverFilter === "verified" ? d.verified && !d.archived :
                    driverFilter === "pending" ? !d.verified && !d.archived :
                    driverFilter === "archived" ? d.archived :
                    driverFilter === "inactive" ? (!d.active && !d.archived) : true
                  )).map((d) => (
                    <TableRow key={d.id} data-testid={`admin-driver-${d.id}`}>
                      <TableCell className="font-medium">{d.business_name || d.name}<div className="text-xs text-muted-foreground">{d.name}</div>{d.archived && d.archive_reason && <div className="mt-1 text-xs text-orange-400" data-testid={`driver-archive-reason-${d.id}`}>Alasan arsip: {d.archive_reason}</div>}</TableCell>
                      <TableCell className="font-mono-terminal">{d.phone}</TableCell>
                      <TableCell>{d.city}</TableCell>
                      <TableCell data-testid={`driver-schedule-count-${d.id}`}><span className="font-mono-terminal font-bold text-primary">{d.schedule_count ?? 0}</span> <span className="text-xs text-muted-foreground">jadwal</span></TableCell>
                      <TableCell><span className="flex items-center gap-1"><Star className="h-3 w-3 fill-primary text-primary" />{d.avg_rating || "0"}</span></TableCell>
                      <TableCell className="space-x-1">
                        {d.verified ? <Badge className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/15">Verified</Badge> : <Badge className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/15">Pending</Badge>}
                        {d.priority && <Badge className="bg-primary/15 text-primary hover:bg-primary/15">Prioritas</Badge>}
                        {d.archived ? <Badge className="bg-orange-500/15 text-orange-400 hover:bg-orange-500/15">Diarsipkan</Badge> : (!d.active && <Badge variant="destructive">Nonaktif</Badge>)}
                      </TableCell>
                      <TableCell className="space-x-1 whitespace-nowrap">
                        {!d.verified && <Button size="sm" data-testid={`verify-${d.id}`} onClick={() => verify(d.id)} className="bg-emerald-500 text-black hover:bg-emerald-600"><ShieldCheck className="mr-1 h-3 w-3" />Verifikasi</Button>}
                        <Button size="sm" variant="secondary" onClick={() => togglePriority(d.id)}>{d.priority ? "Cabut Prioritas" : "Set Prioritas"}</Button>
                        {user?.role === "super_admin" && <Button size="sm" variant="outline" data-testid={`driver-phone-${d.id}`} onClick={() => { setPhoneEdit({ id: d.id, name: d.business_name || d.name, phone: d.phone }); setPhoneVal(d.phone || ""); }}>Ganti Nomor</Button>}
                        {user?.role === "super_admin" && <Button size="sm" variant="outline" data-testid={`driver-pwd-${d.id}`} onClick={() => { setPwdEdit({ id: d.id, name: d.business_name || d.name }); setPwdVal(""); }}>Reset Sandi</Button>}
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(d.id)}>{d.active ? "Nonaktifkan" : "Aktifkan"}</Button>
                        <Button size="sm" variant="outline" data-testid={`driver-archive-${d.id}`} onClick={() => archiveDriver(d)} className={d.archived ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" : "border-orange-500/40 text-orange-400 hover:bg-orange-500/10"}>{d.archived ? "Pulihkan" : "Arsipkan"}</Button>
                        <Button size="sm" variant="destructive" data-testid={`driver-delete-${d.id}`} onClick={() => setDelDriver({ id: d.id, name: d.business_name || d.name })}><Trash2 className="mr-1 h-3 w-3" />Hapus</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {drivers.filter((d) => (
                    driverFilter === "all" ? true :
                    driverFilter === "verified" ? d.verified && !d.archived :
                    driverFilter === "pending" ? !d.verified && !d.archived :
                    driverFilter === "archived" ? d.archived :
                    driverFilter === "inactive" ? (!d.active && !d.archived) : true
                  )).length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground" data-testid="driver-filter-empty">Tidak ada driver sesuai filter.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <Dialog open={!!delDriver} onOpenChange={(o) => { if (!o) setDelDriver(null); }}>              <DialogContent data-testid="driver-delete-dialog">
                <DialogHeader><DialogTitle>Hapus Driver</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Hapus driver <b className="text-foreground">{delDriver?.name}</b>? Semua jadwal, armada, dan iklan sponsor driver ini akan ikut terhapus permanen. Tindakan ini tidak dapat dibatalkan.</p>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setDelDriver(null)}>Batal</Button>
                  <Button variant="destructive" data-testid="driver-delete-confirm" onClick={deleteDriver}><Trash2 className="mr-1 h-4 w-4" />Hapus Permanen</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={!!phoneEdit} onOpenChange={(o) => { if (!o) { setPhoneEdit(null); setPhoneVal(""); } }}>
              <DialogContent data-testid="driver-phone-dialog">
                <DialogHeader><DialogTitle>Ganti Nomor WhatsApp — {phoneEdit?.name}</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Perbaiki nomor pendaftaran driver bila salah input. Nomor lama: <b className="text-foreground">{phoneEdit?.phone}</b>. Driver akan login dengan nomor baru ini.</p>
                <div className="grid gap-1"><Label className="text-xs">Nomor WhatsApp Baru</Label><Input data-testid="driver-phone-input" value={phoneVal} onChange={(e) => setPhoneVal(e.target.value)} placeholder="08xxxxxxxxxx" /></div>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => { setPhoneEdit(null); setPhoneVal(""); }}>Batal</Button>
                  <Button data-testid="driver-phone-save" onClick={saveDriverPhone} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan Nomor</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={!!pwdEdit} onOpenChange={(o) => { if (!o) { setPwdEdit(null); setPwdVal(""); } }}>
              <DialogContent data-testid="driver-pwd-dialog">
                <DialogHeader><DialogTitle>Reset Kata Sandi Driver — {pwdEdit?.name}</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Buat kata sandi baru untuk driver ini. Driver akan login memakai kata sandi baru tersebut.</p>
                <div className="grid gap-1"><Label className="text-xs">Kata Sandi Baru</Label><Input data-testid="driver-pwd-input" value={pwdVal} onChange={(e) => setPwdVal(e.target.value)} placeholder="Minimal 6 karakter" /></div>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => { setPwdEdit(null); setPwdVal(""); }}>Batal</Button>
                  <Button data-testid="driver-pwd-save" onClick={saveDriverPwd} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan Sandi</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={!!archiveTarget} onOpenChange={(o) => { if (!o) { setArchiveTarget(null); setArchiveReason(""); } }}>
              <DialogContent data-testid="driver-archive-dialog">
                <DialogHeader><DialogTitle>Arsipkan / Blokir Driver</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Sembunyikan <b className="text-foreground">{archiveTarget?.name}</b> dari publik (pencarian, terminal, profil). Datanya tetap tersimpan dan dapat dipulihkan kapan saja.</p>
                <div className="grid gap-1"><Label className="text-xs">Alasan Pengarsipan (opsional)</Label>
                  <Textarea data-testid="driver-archive-reason-input" value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} placeholder="cth. Banyak komplain penumpang / dokumen kedaluwarsa / melanggar ketentuan" />
                </div>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => { setArchiveTarget(null); setArchiveReason(""); }}>Batal</Button>
                  <Button data-testid="driver-archive-confirm" onClick={confirmArchive} className="border-orange-500/40 bg-orange-500 font-bold text-black hover:bg-orange-600">Arsipkan</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="schedules">
            {/* City / SEO management */}
            <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="seo-city-card">
              <h3 className="font-display text-lg font-bold">Kelola Kota (SEO)</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Setiap kota otomatis membuat halaman <span className="font-mono-terminal text-primary">/travel-dari-*</span> &amp; <span className="font-mono-terminal text-primary">/travel-ke-*</span>, dan setiap pasangan kota membuat halaman rute (travel, charter, paket kilat) yang masuk sitemap Google. Tempel banyak nama kota dipisah koma atau baris baru.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_200px]">
                <Textarea data-testid="bulk-cities-input" value={bulkCities} onChange={(e) => setBulkCities(e.target.value)} placeholder="Bekasi, Depok, Cirebon, Tegal, Pekalongan, Magelang, ..." className="min-h-[80px]" />
                <div className="grid gap-2">
                  <Input data-testid="bulk-cities-province" value={bulkProvince} onChange={(e) => setBulkProvince(e.target.value)} placeholder="Provinsi (opsional)" />
                  <Button data-testid="bulk-cities-submit" onClick={saveBulkCities} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Tambah Kota</Button>
                  <span className="text-center text-xs text-muted-foreground" data-testid="city-count">{cities.length} kota terdaftar</span>
                </div>
              </div>
              {cities.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {cities.map((c) => (
                    <span key={c.slug} className="rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] text-muted-foreground" data-testid={`city-chip-${c.slug}`}>{c.name}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">Kelola jadwal & sponsor. Admin dapat menambah atau menyalin jadwal secara massal.</p>
              <div className="flex items-center gap-2">
                {user?.role === "super_admin" && <Button data-testid="admin-dedup-btn" variant="outline" onClick={cleanDuplicates} className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"><Copy className="mr-1 h-4 w-4" /> Bersihkan Duplikat</Button>}
                {user?.role === "super_admin" && <Button data-testid="admin-mass-delete-btn" variant="outline" onClick={() => { setMassDelForm({ driver_id: "", origin: "", destination: "", date: "" }); setMassDelDialog(true); }} className="border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="mr-1 h-4 w-4" /> Hapus Massal</Button>}
                <Button data-testid="admin-copy-schedule-btn" variant="secondary" onClick={() => { setCopyForm(emptyCopy); setCopyItems([]); setCopyDialog(true); }}><Copy className="mr-1 h-4 w-4" /> Salin Jadwal</Button>
                {user?.role === "super_admin" && <Button data-testid="admin-seed-preview-btn" variant="outline" onClick={runSeedPreview} disabled={seedRunning} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Upload className="mr-1 h-4 w-4" /> {seedRunning ? "Mengimpor..." : "Import Data Preview (1-Klik)"}</Button>}
                {user?.role === "super_admin" && <Button data-testid="admin-generate-travels-btn" variant="outline" onClick={runGenerateTravels} disabled={generating} className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10"><Zap className="mr-1 h-4 w-4" /> {generating ? "Membuat..." : "Generate Jadwal Travel"}</Button>}
                {user?.role === "super_admin" && <Button data-testid="admin-seed-remote-btn" variant="outline" onClick={runSeedImport} disabled={seeding} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Download className="mr-1 h-4 w-4" /> {seeding ? "Mengimpor..." : "Import Data Seed"}</Button>}
                <Button data-testid="admin-import-btn" variant="outline" onClick={() => { setImportResult(null); setImportOpen(true); }} className="border-primary/40 font-bold text-primary"><Upload className="mr-1 h-4 w-4" /> Import Massal</Button>
                <Button data-testid="admin-add-schedule-btn" onClick={() => { setSchedEditId(null); setSchedForm(emptySched); setSchedDialog(true); }} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" /> Tambah Jadwal</Button>
              </div>
            </div>

            <Dialog open={copyDialog} onOpenChange={setCopyDialog}>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-copy-schedule-dialog">
                <DialogHeader><DialogTitle>Salin Jadwal Massal</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div className="grid gap-1.5">
                    <Label>Copy dari rute (sumber)</Label>
                    <Select value={copyForm.source_key} onValueChange={onSelectSource}>
                      <SelectTrigger data-testid="copy-source-select"><SelectValue placeholder="Pilih rute yang sudah punya jadwal" /></SelectTrigger>
                      <SelectContent>
                        {routeGroups.map((g) => <SelectItem key={g.key} value={g.key}>{g.origin} → {g.destination} ({g.label}) · {g.count} jadwal</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {copyItems.length > 0 && (
                    <div className="grid gap-1.5">
                      <Label>Pilih & sesuaikan jam ({copyItems.filter((i) => i.checked).length}/{copyItems.length})</Label>
                      <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                        {copyItems.map((it, idx) => (
                          <div key={it.source_id} className="flex items-center gap-2 border-b border-border px-2 py-1.5 last:border-0" data-testid={`copy-item-${it.source_id}`}>
                            <input type="checkbox" checked={it.checked} onChange={(e) => { const c = [...copyItems]; c[idx] = { ...it, checked: e.target.checked }; setCopyItems(c); }} data-testid={`copy-check-${it.source_id}`} />
                            <Input type="time" value={it.time} onChange={(e) => { const c = [...copyItems]; c[idx] = { ...it, time: e.target.value }; setCopyItems(c); }} className="w-28" data-testid={`copy-time-${it.source_id}`} />
                            <span className="truncate text-xs text-muted-foreground">{it.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid gap-1.5"><Label>Tujuan: Asal (kota keberangkatan)</Label>
                    <Select value={copyForm.dest_origin} onValueChange={(v) => setCopyForm({ ...copyForm, dest_origin: v })}>
                      <SelectTrigger data-testid="copy-dest-origin"><SelectValue placeholder="Kota asal" /></SelectTrigger>
                      <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Label>Kota Tujuan — bisa pilih banyak ({copyForm.dest_destinations.length} dipilih)</Label>
                      <div className="flex gap-1">
                        <button type="button" data-testid="copy-select-all" onClick={() => setCopyForm((f) => ({ ...f, dest_destinations: cities.filter((c) => c.name !== f.dest_origin).map((c) => c.name) }))} className="rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">Pilih Semua</button>
                        <button type="button" data-testid="copy-clear-all" onClick={() => setCopyForm((f) => ({ ...f, dest_destinations: [] }))} className="rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">Kosongkan</button>
                      </div>
                    </div>
                    <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-border p-2" data-testid="copy-dest-list">
                      {cities.filter((c) => c.name !== copyForm.dest_origin).map((c) => {
                        const active = copyForm.dest_destinations.includes(c.name);
                        return (
                          <button type="button" key={c.slug} data-testid={`copy-dest-${c.slug}`}
                            onClick={() => setCopyForm((f) => ({ ...f, dest_destinations: active ? f.dest_destinations.filter((x) => x !== c.name) : [...f.dest_destinations, c.name] }))}
                            className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid gap-1.5"><Label>Harga Tiket (Rp/kursi)</Label><Input type="number" data-testid="copy-price" value={copyForm.price} onChange={(e) => setCopyForm({ ...copyForm, price: e.target.value })} placeholder="cth. 350000" /></div>
                  <div className="grid gap-1.5">
                    <Label>Ulangi untuk beberapa hari ke depan</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[{ l: "Hari sumber", d: 0 }, { l: "1 Minggu", d: 6 }, { l: "2 Minggu", d: 13 }, { l: "1 Bulan", d: 29 }, { l: "3 Bulan", d: 89 }, { l: "6 Bulan", d: 179 }, { l: "1 Tahun", d: 364 }].map((p) => (
                        <button type="button" key={p.d} data-testid={`copy-repeat-${p.d}`} onClick={() => setCopyForm({ ...copyForm, repeat_days: p.d })} className={`rounded-full border px-3 py-1 text-xs transition-colors ${Number(copyForm.repeat_days) === p.d ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>{p.l}</button>
                      ))}
                    </div>
                    <Input type="number" min="0" max="365" data-testid="copy-repeat-days" value={copyForm.repeat_days} onChange={(e) => setCopyForm({ ...copyForm, repeat_days: e.target.value })} placeholder="0" />
                    <p className="text-[11px] text-muted-foreground">Disalin untuk tanggal sumber hingga N hari ke depan (maks 365 / 1 tahun penuh). Duplikat otomatis dilewati.</p>
                    <p className="text-xs font-semibold text-primary" data-testid="copy-projection">Perkiraan: {copyItems.filter((i) => i.checked).length * copyForm.dest_destinations.length * ((Number(copyForm.repeat_days) || 0) + 1)} jadwal akan dibuat ({copyItems.filter((i) => i.checked).length} jam × {copyForm.dest_destinations.length} rute × {(Number(copyForm.repeat_days) || 0) + 1} hari)</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setCopyDialog(false)} data-testid="copy-cancel">Batal</Button>
                  <Button className="bg-primary font-bold text-primary-foreground hover:bg-primary/90" onClick={submitCopy} data-testid="copy-submit">Salin Jadwal</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="overflow-x-auto border border-border">
              {user?.role === "super_admin" && schedSel.length > 0 && (
                <div className="flex items-center gap-3 border-b border-border bg-red-500/10 px-3 py-2" data-testid="sched-bulk-bar">
                  <span className="text-sm text-red-300">{schedSel.length} jadwal dipilih</span>
                  <Button size="sm" variant="destructive" data-testid="sched-bulk-delete" onClick={bulkDeleteSelected}><Trash2 className="mr-1 h-4 w-4" />Hapus Terpilih</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSchedSel([])}>Batal Pilih</Button>
                </div>
              )}
              <Table>
                <TableHeader><TableRow>
                  {user?.role === "super_admin" && <TableHead className="w-8"><input type="checkbox" data-testid="sched-select-all" className="h-4 w-4 accent-primary" checked={schedules.length > 0 && schedSel.length === schedules.length} onChange={toggleSelectAllSched} /></TableHead>}
                  <TableHead>Driver</TableHead><TableHead>Rute</TableHead><TableHead>Tanggal/Jam</TableHead><TableHead>Armada</TableHead>
                  <TableHead>Harga</TableHead><TableHead>Status</TableHead><TableHead>Sponsor</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {schedules.slice(0, RENDER_LIMIT).map((s) => (
                    <TableRow key={s.id} data-testid={`admin-schedule-${s.id}`}>
                      {user?.role === "super_admin" && <TableCell><input type="checkbox" data-testid={`sched-check-${s.id}`} className="h-4 w-4 accent-primary" checked={schedSel.includes(s.id)} onChange={() => toggleSchedSel(s.id)} /></TableCell>}
                      <TableCell className="text-sm">{driverName(s.driver_id)}</TableCell>
                      <TableCell>{s.origin} → {s.destination}</TableCell>
                      <TableCell className="font-mono-terminal">{s.date} {s.time}{schedDupKeys[`${s.driver_id}|${s.date}|${s.time}|${s.origin}|${s.destination}`] > 1 && <Badge className="ml-2 bg-amber-500/15 text-amber-400 hover:bg-amber-500/15" data-testid={`sched-dup-${s.id}`}>Duplikat</Badge>}</TableCell>
                      <TableCell>{s.vehicle_name}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(s.price)}</TableCell>
                      <TableCell><Badge variant="secondary">{s.status}</Badge></TableCell>
                      <TableCell>
                        <Button size="sm" data-testid={`sponsor-${s.id}`} variant={s.sponsor ? "default" : "secondary"} onClick={() => toggleSponsor(s.id)} className={s.sponsor ? "bg-primary text-primary-foreground" : ""}>
                          <Zap className="mr-1 h-3 w-3" />{s.sponsor ? "Sponsor" : "Jadikan Sponsor"}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" data-testid={`admin-share-btn-${s.id}`} title="Bagikan jadwal ke WhatsApp" onClick={() => shareScheduleToWA(s, driverName(s.driver_id))}>
                            <Share2 className="h-4 w-4 text-[#25D366]" />
                          </Button>
                          <Button size="icon" variant="ghost" data-testid={`admin-edit-schedule-${s.id}`} title="Edit jadwal" onClick={() => openEditSchedule(s)}>
                            <Pencil className="h-4 w-4 text-primary" />
                          </Button>
                          <Button size="icon" variant="ghost" data-testid={`admin-delete-schedule-${s.id}`} title="Hapus jadwal" onClick={() => setDelSched(s)}>
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {schedules.length === 0 && <TableRow><TableCell colSpan={user?.role === "super_admin" ? 9 : 8} className="text-center text-muted-foreground">Belum ada jadwal.</TableCell></TableRow>}
                  {schedules.length > RENDER_LIMIT && <TableRow><TableCell colSpan={user?.role === "super_admin" ? 9 : 8} className="text-center text-xs text-muted-foreground">Menampilkan {RENDER_LIMIT} dari {schedules.length} jadwal (maks. 2000 dimuat). Gunakan Hapus Massal / Salin Jadwal untuk kelola per rute.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>

            <Dialog open={!!delSched} onOpenChange={(o) => !o && setDelSched(null)}>              <DialogContent className="sm:max-w-md" data-testid="admin-delete-schedule-dialog">
                <DialogHeader><DialogTitle>Hapus Jadwal?</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Jadwal <span className="font-semibold text-foreground">{delSched?.origin} → {delSched?.destination}</span> pada{" "}
                  <span className="font-mono-terminal">{delSched?.date} {delSched?.time}</span> ({driverName(delSched?.driver_id)}) akan dihapus permanen dan tidak lagi tampil di terminal. Tindakan ini tidak dapat dibatalkan.
                </p>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setDelSched(null)} data-testid="cancel-delete-schedule">Batal</Button>
                  <Button className="bg-red-500 font-bold text-white hover:bg-red-500/90" onClick={doDelSchedule} data-testid="confirm-delete-schedule">Hapus Jadwal</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={massDelDialog} onOpenChange={setMassDelDialog}>
              <DialogContent data-testid="admin-mass-delete-dialog">
                <DialogHeader><DialogTitle>Hapus Jadwal Massal</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Hapus semua jadwal yang cocok dengan filter di bawah secara permanen. Isi minimal satu filter (asal / tujuan / tanggal).</p>
                <div className="grid gap-3">
                  <div className="grid gap-1.5"><Label className="text-xs">Driver (opsional)</Label>
                    <Select value={massDelForm.driver_id || "__any"} onValueChange={(v) => setMassDelForm({ ...massDelForm, driver_id: v === "__any" ? "" : v })}>
                      <SelectTrigger data-testid="mass-del-driver"><SelectValue placeholder="Semua driver" /></SelectTrigger>
                      <SelectContent><SelectItem value="__any">Semua driver</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.business_name || d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5"><Label className="text-xs">Kota Asal</Label>
                      <Select value={massDelForm.origin || "__any"} onValueChange={(v) => setMassDelForm({ ...massDelForm, origin: v === "__any" ? "" : v })}>
                        <SelectTrigger data-testid="mass-del-origin"><SelectValue placeholder="Semua" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any">Semua</SelectItem>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5"><Label className="text-xs">Kota Tujuan</Label>
                      <Select value={massDelForm.destination || "__any"} onValueChange={(v) => setMassDelForm({ ...massDelForm, destination: v === "__any" ? "" : v })}>
                        <SelectTrigger data-testid="mass-del-destination"><SelectValue placeholder="Semua" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any">Semua</SelectItem>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-1.5"><Label className="text-xs">Tanggal</Label><Input type="date" data-testid="mass-del-date" value={massDelForm.date} onChange={(e) => setMassDelForm({ ...massDelForm, date: e.target.value })} /></div>
                  <div className="text-xs font-semibold text-red-400" data-testid="mass-del-preview">{(() => { const n = schedules.filter((s) => (!massDelForm.driver_id || s.driver_id === massDelForm.driver_id) && (!massDelForm.origin || s.origin === massDelForm.origin) && (!massDelForm.destination || s.destination === massDelForm.destination) && (!massDelForm.date || s.date === massDelForm.date)).length; return `${n} jadwal cocok akan dihapus`; })()}</div>
                </div>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setMassDelDialog(false)}>Batal</Button>
                  <Button variant="destructive" data-testid="mass-del-confirm" onClick={massDeleteByRoute}><Trash2 className="mr-1 h-4 w-4" />Hapus Semua yang Cocok</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="import-dialog">
                <DialogHeader><DialogTitle>Import Jadwal Massal (JSON)</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <p className="text-xs text-muted-foreground">Tempel JSON atau unggah file .json. Akun driver dibuat otomatis dari nomor WhatsApp (jika belum ada). Nama kolom fleksibel (mis. nama_travel / asal / tujuan / tanggal / jam / armada / kapasitas / harga).</p>
                  <input type="file" accept=".json,application/json" onChange={onImportFile} data-testid="import-file" className="text-sm" />
                  <Textarea data-testid="import-json" value={importText} onChange={(e) => setImportText(e.target.value)} rows={12} className="font-mono-terminal text-xs" placeholder={`{
  "default_password": "travele123",
  "drivers": [
    { "nama_travel": "Travel Berkah", "wa": "081234500001", "kota": "Semarang",
      "jadwal": [
        { "asal": "Semarang", "tujuan": "Jakarta", "tanggal": "2026-08-25", "jam": "08:00", "armada": "Hiace", "kapasitas": 14, "harga": 350000 }
      ] }
  ]
}`} />
                  {importResult && (
                    <div className="border border-border bg-muted/30 p-3 text-sm" data-testid="import-result">
                      <div className="font-bold text-primary">Hasil Import</div>
                      <div>Driver baru: {importResult.drivers_created} · Driver lama: {importResult.drivers_existing} · Jadwal: {importResult.schedules_created}</div>
                      {importResult.new_driver_credentials?.length > 0 && (
                        <div className="mt-2"><div className="text-xs font-bold uppercase text-muted-foreground">Akun driver baru (simpan!):</div>
                          {importResult.new_driver_credentials.map((c, i) => <div key={i} className="font-mono-terminal text-xs">{c.business_name} · {c.phone} · sandi: {c.password}</div>)}
                        </div>
                      )}
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-400">{importResult.errors.map((er, i) => <div key={i}>• {er}</div>)}</div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setImportOpen(false)}>Tutup</Button>
                  <Button data-testid="import-submit" onClick={runImport} disabled={importing} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">{importing ? "Mengimpor..." : "Proses Import"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={schedDialog} onOpenChange={(o) => { setSchedDialog(o); if (!o) setSchedEditId(null); }}>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-schedule-dialog">
                <DialogHeader><DialogTitle>{schedEditId ? "Edit Jadwal Driver" : "Tambah Jadwal Driver"}</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div className="grid gap-1.5"><Label>Driver</Label>
                    <Select value={schedForm.driver_id} onValueChange={(v) => setSchedForm({ ...schedForm, driver_id: v })}>
                      <SelectTrigger data-testid="sched-driver"><SelectValue placeholder="Pilih driver" /></SelectTrigger>
                      <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{(d.business_name || d.name)}{d.verified ? "" : " (belum verif)"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5"><Label>Jenis Layanan</Label>
                    <Select value={schedForm.service_type} onValueChange={(v) => setSchedForm({ ...schedForm, service_type: v })}>
                      <SelectTrigger data-testid="sched-service"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="door_to_door">Door to Door</SelectItem>
                        <SelectItem value="charter">Charter Drop Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5"><Label>Kota Asal</Label>
                      <Select value={schedForm.origin} onValueChange={(v) => setSchedForm({ ...schedForm, origin: v })}>
                        <SelectTrigger data-testid="sched-origin"><SelectValue placeholder="Pilih" /></SelectTrigger>
                        <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5"><Label>Kota Tujuan</Label>
                      <Select value={schedForm.destination} onValueChange={(v) => setSchedForm({ ...schedForm, destination: v })}>
                        <SelectTrigger data-testid="sched-destination"><SelectValue placeholder="Pilih" /></SelectTrigger>
                        <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5"><Label>Tanggal</Label><Input type="date" data-testid="sched-date" value={schedForm.date} onChange={(e) => setSchedForm({ ...schedForm, date: e.target.value })} /></div>
                    <div className="grid gap-1.5"><Label>Jam</Label><Input type="time" data-testid="sched-time" value={schedForm.time} onChange={(e) => setSchedForm({ ...schedForm, time: e.target.value })} /></div>
                  </div>
                  <div className="grid gap-1.5"><Label>Nama Armada</Label><Input data-testid="sched-vehicle" value={schedForm.vehicle_name} onChange={(e) => setSchedForm({ ...schedForm, vehicle_name: e.target.value })} placeholder="cth. Toyota Hiace" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5"><Label>Kapasitas</Label><Input type="number" data-testid="sched-capacity" value={schedForm.capacity} onChange={(e) => setSchedForm({ ...schedForm, capacity: e.target.value })} /></div>
                    <div className="grid gap-1.5"><Label>Harga (Rp/kursi)</Label><Input type="number" data-testid="sched-price" value={schedForm.price} onChange={(e) => setSchedForm({ ...schedForm, price: e.target.value })} placeholder="0" /></div>
                  </div>
                  {schedForm.service_type === "charter" && (
                    <div className="rounded-md border border-primary/30 bg-primary/[0.06] p-3" data-testid="charter-calc">
                      <div className="mb-1 text-xs font-semibold text-primary">Kalkulator Tarif Charter (internal Admin)</div>
                      <p className="mb-2 text-[11px] text-muted-foreground">Rumus: harga tiket reguler/penumpang × kapasitas armada (Avanza 6, Innova 7, Hiace 10, Elf 12). Perhitungan ini tidak ditampilkan ke penumpang.</p>
                      <div className="flex items-end gap-2">
                        <div className="grid flex-1 gap-1"><Label className="text-xs">Harga tiket reguler/penumpang</Label><Input type="number" data-testid="charter-regular" value={charterReg} onChange={(e) => setCharterReg(e.target.value)} placeholder="cth. 200000" /></div>
                        <Button type="button" variant="secondary" onClick={applyCharter} data-testid="charter-apply">Hitung ×{charterMultiplier(schedForm.vehicle_name) || "?"}</Button>
                      </div>
                    </div>
                  )}
                  <div className="grid gap-1.5"><Label>Titik Jemput / Area (opsional)</Label><Input data-testid="sched-pickup" value={schedForm.pickup_area} onChange={(e) => setSchedForm({ ...schedForm, pickup_area: e.target.value })} /></div>
                  {!schedEditId && (
                  <div className="grid gap-1.5">
                    <Label>Terbitkan untuk periode</Label>
                    <div className="flex flex-wrap gap-1.5" data-testid="sched-repeat-presets">
                      {[
                        { label: "Hari ini saja", days: 0 },
                        { label: "7 hari", days: 6 },
                        { label: "1 bulan", days: 29 },
                        { label: "6 bulan", days: 179 },
                      ].map((p) => (
                        <button
                          key={p.days}
                          type="button"
                          data-testid={`sched-repeat-${p.days}`}
                          onClick={() => setSchedForm({ ...schedForm, repeat_days: p.days })}
                          className={`rounded-full border px-3 py-1 text-xs transition-colors ${Number(schedForm.repeat_days) === p.days ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <Input type="number" min="0" max="184" data-testid="sched-repeat" value={schedForm.repeat_days} onChange={(e) => setSchedForm({ ...schedForm, repeat_days: e.target.value })} placeholder="0" />
                    <span className="text-xs text-muted-foreground">Akan membuat <b className="text-primary">{(Number(schedForm.repeat_days) || 0) + 1} jadwal</b> (jam &amp; rute sama, mulai tanggal di atas). Maks 6 bulan (185 jadwal).</span>
                  </div>
                  )}
                  <div className="grid gap-1.5"><Label>Catatan (opsional)</Label><Textarea data-testid="sched-note" value={schedForm.note} onChange={(e) => setSchedForm({ ...schedForm, note: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => { setSchedDialog(false); setSchedEditId(null); }}>Batal</Button>
                  <Button data-testid="sched-submit" onClick={saveSchedule} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">{schedEditId ? "Simpan Perubahan" : "Simpan Jadwal"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="bills">
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>No. Order</TableHead><TableHead>Nilai Transaksi</TableHead><TableHead>Komisi</TableHead>
                  <TableHead>Jatuh Tempo</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bills.map((b) => (
                    <TableRow key={b.id} data-testid={`admin-bill-${b.id}`}>
                      <TableCell className="font-mono-terminal">{b.order_number}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(b.transaction_value)}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(b.amount)}</TableCell>
                      <TableCell>{b.due_date}</TableCell>
                      <TableCell><Badge variant={b.status === "lunas" ? "default" : "secondary"}>{b.status}</Badge></TableCell>
                      <TableCell>{b.status !== "lunas" && <Button size="sm" variant="secondary" onClick={() => payBill(b.id)}>Tandai Lunas</Button>}</TableCell>
                    </TableRow>
                  ))}
                  {bills.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Belum ada tagihan.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="ads">
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Driver</TableHead><TableHead>Rute</TableHead><TableHead>Paket</TableHead>
                  <TableHead>Harga</TableHead><TableHead>Periode</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {ads.map((a) => (
                    <TableRow key={a.id} data-testid={`admin-ad-${a.id}`}>
                      <TableCell className="font-medium">{a.driver_name}</TableCell>
                      <TableCell>{a.origin} → {a.destination}<div className="text-xs text-muted-foreground">{a.vehicle_name}</div></TableCell>
                      <TableCell className="capitalize">{a.package}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(a.price)}</TableCell>
                      <TableCell className="font-mono-terminal text-xs">{a.start_date} – {a.end_date}</TableCell>
                      <TableCell><Badge variant={a.status === "aktif" ? "default" : "secondary"} className={a.status === "aktif" ? "bg-primary text-primary-foreground" : ""}>{a.status}</Badge></TableCell>
                      <TableCell className="space-x-1 whitespace-nowrap">
                        {a.status !== "aktif" && <Button size="sm" data-testid={`ad-activate-${a.id}`} onClick={() => activateAd(a.id)} className="bg-primary text-primary-foreground"><Megaphone className="mr-1 h-3 w-3" />Aktifkan</Button>}
                        {a.status === "aktif" && <Button size="sm" variant="ghost" onClick={() => stopAd(a.id)}>Hentikan</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {ads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Belum ada permintaan iklan.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="complaints">
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Order</TableHead><TableHead>Pelapor</TableHead><TableHead>Driver</TableHead>
                  <TableHead>Kategori</TableHead><TableHead>Kronologi</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {complaints.map((c) => (
                    <TableRow key={c.id} data-testid={`admin-complaint-${c.id}`}>
                      <TableCell className="font-mono-terminal">{c.order_number}</TableCell>
                      <TableCell>{c.reporter_name}<div className="text-xs text-muted-foreground">{c.reporter_phone}</div></TableCell>
                      <TableCell>{c.driver_name}</TableCell>
                      <TableCell>{COMPLAINT_CAT[c.category] || c.category}</TableCell>
                      <TableCell className="max-w-xs"><div className="truncate text-sm text-muted-foreground" title={c.description}>{c.description}</div></TableCell>
                      <TableCell>
                        <Select value={c.status} onValueChange={(v) => updateComplaint(c.id, v)}>
                          <SelectTrigger data-testid={`complaint-status-${c.id}`} className="w-48"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(COMPLAINT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {complaints.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Belum ada pengaduan.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="packages">
            <div className="mb-3 flex flex-wrap items-center gap-2 border border-border bg-card p-3">
              <div className="flex items-center gap-1">
                <Button size="icon" variant="secondary" data-testid="pkg-prev-day" onClick={() => shiftPkgDate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Input type="date" data-testid="pkg-date" value={pkgDate} onChange={(e) => { setPkgShowAll(false); setPkgDate(e.target.value); }} className="w-40" disabled={pkgShowAll} />
                <Button size="icon" variant="secondary" data-testid="pkg-next-day" onClick={() => shiftPkgDate(1)}><ChevronRight className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" data-testid="pkg-today" onClick={() => { setPkgShowAll(false); setPkgDate(todayStr); }}>Hari Ini</Button>
              </div>
              <Button size="sm" variant={pkgShowAll ? "default" : "outline"} data-testid="pkg-toggle-all" onClick={() => setPkgShowAll(!pkgShowAll)} className={pkgShowAll ? "bg-primary text-primary-foreground" : ""}>{pkgShowAll ? "Tampilkan Harian" : "Semua Tanggal"}</Button>
              <Button size="sm" variant="secondary" data-testid="pkg-export-csv" onClick={exportPkgCsv} disabled={dayPackages.length === 0}><Download className="mr-1 h-4 w-4" /> Ekspor Harian (CSV)</Button>
              <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
                <span>Paket: <b data-testid="pkg-day-count">{pkgSummary.count}</b></span>
                <span>Tarif: <b className="font-mono-terminal">{rupiah(pkgSummary.total)}</b></span>
                <span>Komisi: <b className="font-mono-terminal text-primary">{rupiah(pkgSummary.commission)}</b></span>
                <span>Sisa: <b className="font-mono-terminal">{rupiah(pkgSummary.sisa)}</b></span>
              </div>
            </div>
            {!pkgShowAll && (
              <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 border border-border bg-background px-4 py-2 text-sm">
                <span>Sudah disetor: <b className="font-mono-terminal text-emerald-400" data-testid="pkg-settle-done">{rupiah(pkgSummary.settled)}</b></span>
                <span>Belum disetor: <b className="font-mono-terminal text-amber-400" data-testid="pkg-settle-pending">{rupiah(pkgSummary.pending)}</b></span>
                <span className="text-muted-foreground">Total sisa: <b className="font-mono-terminal text-foreground">{rupiah(pkgSummary.sisa)}</b></span>
              </div>
            )}
            <div className="overflow-x-auto border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>No. Kiriman</TableHead><TableHead>Pengirim / Penerima</TableHead><TableHead>Rute</TableHead><TableHead>Jenis</TableHead><TableHead>Driver Pembawa</TableHead>
                  <TableHead>Tarif</TableHead><TableHead>Komisi</TableHead><TableHead>Sisa</TableHead><TableHead>Status Kirim</TableHead><TableHead>Setor</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dayPackages.map((p) => {
                    const price = Number(pkgFin[p.id]?.price) || 0; const comm = Number(pkgFin[p.id]?.commission) || 0;
                    return (
                    <TableRow key={p.id} data-testid={`admin-package-${p.package_number}`}>
                      <TableCell className="font-mono-terminal">{p.package_number}<div className="text-[10px] text-muted-foreground">{pkgBasisDate(p)}</div></TableCell>
                      <TableCell className="text-sm">{p.sender_name}<div className="text-xs text-muted-foreground">→ {p.receiver_name}</div></TableCell>
                      <TableCell className="text-sm">{p.origin} → {p.destination}</TableCell>
                      <TableCell><Badge variant="secondary">{p.item_type === "dokumen" ? "Dokumen" : "Barang"}</Badge></TableCell>
                      <TableCell>
                        <Select value={p.assigned_driver_id || "none"} onValueChange={(v) => assignPackage(p.id, v)}>
                          <SelectTrigger data-testid={`pkg-assign-${p.package_number}`} className="w-44"><SelectValue placeholder="Pilih driver" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Belum ditugaskan —</SelectItem>
                            {drivers.filter((d) => d.verified).map((d) => <SelectItem key={d.id} value={d.id}>{d.business_name || d.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input data-testid={`pkg-price-${p.package_number}`} type="number" value={pkgFin[p.id]?.price ?? ""} onChange={(e) => setPkgFin((f) => ({ ...f, [p.id]: { ...f[p.id], price: e.target.value } }))} onBlur={() => savePkgFinance(p)} className="w-28" placeholder="0" />
                      </TableCell>
                      <TableCell>
                        <Input data-testid={`pkg-commission-${p.package_number}`} type="number" value={pkgFin[p.id]?.commission ?? ""} onChange={(e) => setPkgFin((f) => ({ ...f, [p.id]: { ...f[p.id], commission: e.target.value } }))} onBlur={() => savePkgFinance(p)} className="w-28" placeholder="0" />
                      </TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(price - comm)}</TableCell>
                      <TableCell>
                        <Select value={p.status} onValueChange={(v) => updatePackage(p.id, v)}>
                          <SelectTrigger data-testid={`package-status-${p.package_number}`} className="w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>{PACKAGE_STATUS_KEYS.map((s) => <SelectItem key={s} value={s}>{PACKAGE_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant={p.settled ? "outline" : "default"} data-testid={`pkg-settle-${p.package_number}`} onClick={() => togglePkgSettled(p)} className={p.settled ? "" : "bg-emerald-500 text-white hover:bg-emerald-600"}>
                          {p.settled ? "✓ Disetor" : "Tandai"}
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {dayPackages.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">{pkgShowAll ? "Belum ada kiriman paket." : "Tidak ada kiriman pada tanggal ini."}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="blog">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tulis artikel SEO (panduan rute, tips) agar diindeks Google.</p>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="blog-seo-summary">
                  <span className="font-mono-terminal font-bold text-amber-400">{articles.filter((a) => a.route_linked).length}</span> dari {articles.length} artikel memakai slug Money Page (di-canonical, tak ranking sendiri)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="import-wp-btn" variant="outline" onClick={() => { setWpResult(null); setWpDialog(true); }} className="border-primary/40 font-bold text-primary"><Globe className="mr-1 h-4 w-4" /> Import dari WordPress</Button>
                <Button data-testid="import-xml-btn" variant="outline" onClick={() => { setXmlFile(null); setXmlResult(null); setXmlPhase("idle"); setXmlProgress(0); setXmlDialog(true); }} className="border-primary/40 font-bold text-primary"><Upload className="mr-1 h-4 w-4" /> Import File XML</Button>
                <Button data-testid="new-article-btn" onClick={openNewArticle} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" /> Tulis Artikel</Button>
              </div>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Judul</TableHead><TableHead>Kategori</TableHead><TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead><TableHead>SEO</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {articles.map((a) => (
                    <TableRow key={a.id} data-testid={`admin-article-${a.id}`}>
                      <TableCell className="font-medium">{a.title}</TableCell>
                      <TableCell>{a.category}</TableCell>
                      <TableCell className="font-mono-terminal text-xs">/blog/{a.slug}</TableCell>
                      <TableCell><Badge variant={a.status === "published" ? "default" : "secondary"} className={a.status === "published" ? "bg-primary text-primary-foreground" : ""}>{a.status}</Badge></TableCell>
                      <TableCell data-testid={`article-seo-${a.id}`}>
                        {a.route_linked
                          ? <a href={`/${a.route_slug}`} target="_blank" rel="noreferrer" title={`Slug menyerupai Money Page /${a.route_slug} — di-canonical ke Money Page, artikel tidak ranking sendiri`}><Badge className="gap-1 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25"><AlertTriangle className="h-3 w-3" /> Slug = Money Page</Badge></a>
                          : <Badge variant="secondary" className="gap-1 text-muted-foreground">Berdiri Sendiri</Badge>}
                      </TableCell>
                      <TableCell className="space-x-1 whitespace-nowrap">
                        <Button size="icon" variant="ghost" data-testid={`edit-article-${a.id}`} onClick={() => openEditArticle(a.id)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteArticle(a.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {articles.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Belum ada artikel.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reports">
            <div className="mb-4 flex flex-wrap items-end gap-3 border border-border bg-card p-4">
              <div className="grid gap-1.5">
                <Label className="text-xs uppercase text-muted-foreground">Dari Tanggal</Label>
                <Input data-testid="report-from" type="date" value={repFrom} onChange={(e) => setRepFrom(e.target.value)} className="w-44" />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs uppercase text-muted-foreground">Sampai Tanggal</Label>
                <Input data-testid="report-to" type="date" value={repTo} onChange={(e) => setRepTo(e.target.value)} className="w-44" />
              </div>
              <p className="text-xs text-muted-foreground">Filter berlaku untuk laporan Order & Komisi. Kosongkan untuk semua data.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { t: "orders", l: "Laporan Order", d: "Semua order: penumpang, rute, harga, komisi, status." },
                { t: "commissions", l: "Laporan Komisi & Tagihan", d: "Tagihan komisi driver beserta status pembayaran." },
                { t: "drivers", l: "Laporan Driver", d: "Data driver: rating, verifikasi, status, kota." },
                { t: "ads", l: "Laporan Iklan", d: "Performa iklan sponsor: tayangan, klik, konversi." },
              ].map((r) => (
                <div key={r.t} className="flex items-center justify-between border border-border bg-card p-5" data-testid={`report-${r.t}`}>
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="mt-0.5 h-6 w-6 text-primary" />
                    <div>
                      <div className="font-display text-lg font-bold">{r.l}</div>
                      <div className="text-sm text-muted-foreground">{r.d}</div>
                    </div>
                  </div>
                  <Button size="sm" data-testid={`download-${r.t}`} onClick={() => downloadReport(r.t)} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                    <Download className="mr-1 h-4 w-4" /> CSV
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">File CSV dapat dibuka di Excel, Google Sheets, atau diimpor ke sistem lain.</p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Article editor */}      <Dialog open={artDialog} onOpenChange={setArtDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="article-dialog">
          <DialogHeader><DialogTitle>{artForm.id ? "Edit Artikel" : "Tulis Artikel"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Judul</Label><Input data-testid="art-title" value={artForm.title} onChange={(e) => setArtForm({ ...artForm, title: e.target.value })} placeholder="Judul artikel" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Kategori</Label>
                <Select value={artForm.category} onValueChange={(v) => setArtForm({ ...artForm, category: v })}>
                  <SelectTrigger data-testid="art-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Travel", "Charter Mobil", "Paket Kilat", "Informasi Rute", "Tips Perjalanan", "Harga Travel", "Jadwal Perjalanan", "Tempat Wisata", "Panduan Kota", "Keselamatan Perjalanan"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Status</Label>
                <Select value={artForm.status} onValueChange={(v) => setArtForm({ ...artForm, status: v })}>
                  <SelectTrigger data-testid="art-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="published">Terbit</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Slug (opsional)</Label>
              <Input data-testid="art-slug" value={artForm.slug} onChange={(e) => setArtForm({ ...artForm, slug: e.target.value })} placeholder="otomatis dari judul" />
              {slugCheck && (
                <div data-testid="art-slug-warning" className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <b>Slug bentrok dengan Money Page</b> <span className="font-mono-terminal">/{slugCheck.route_slug}</span>. Artikel akan di-canonical ke Money Page &amp; tidak akan ranking sendiri (anti-kanibalisasi). Disarankan pakai slug informasional.
                    <button type="button" data-testid="art-slug-fix" onClick={() => setArtForm((f) => ({ ...f, slug: `tips-${slugCheck.route_slug}` }))} className="ml-1 font-semibold underline hover:text-amber-200">Perbaiki → tips-{slugCheck.route_slug}</button>
                  </div>
                </div>
              )}
            </div>
            <div className="grid gap-1.5"><Label>Ringkasan / Meta Description</Label><Textarea data-testid="art-excerpt" value={artForm.excerpt} onChange={(e) => setArtForm({ ...artForm, excerpt: e.target.value })} placeholder="Ringkasan singkat untuk hasil pencarian" /></div>
            <div className="grid gap-1.5"><Label>Isi Artikel</Label>
              <RichTextEditor key={artKey} value={artForm.content} onChange={(html) => setArtForm((f) => ({ ...f, content: html }))} />
              <p className="text-xs text-muted-foreground">Editor Klasik: gunakan toolbar untuk sub-judul, tebal, miring, daftar, tautan, dan gambar.</p>
            </div>
            <PhotoUpload label="Gambar Utama (Cover)" testId="art-cover-upload" value={artForm.cover_image} onChange={(url) => setArtForm({ ...artForm, cover_image: url })} />
            <div className="grid gap-1.5"><Label>Tag (pisah koma)</Label><Input data-testid="art-tags" value={(artForm.tags || []).join(", ")} onChange={(e) => setArtForm({ ...artForm, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="semarang, jakarta" /></div>
            <div className="rounded-md border border-border bg-secondary/30 p-3" data-testid="art-seo-panel">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-semibold">Skor SEO Artikel</span>
                <span data-testid="art-seo-score" className={`font-mono-terminal text-sm font-bold ${artSeo.score >= 80 ? "text-emerald-400" : artSeo.score >= 50 ? "text-amber-400" : "text-red-400"}`}>{artSeo.score}/100</span>
              </div>
              <div className="mb-2.5 h-1.5 w-full overflow-hidden rounded-full bg-border"><div className={`h-full transition-all ${artSeo.score >= 80 ? "bg-emerald-500" : artSeo.score >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${artSeo.score}%` }} /></div>
              <ul className="space-y-1.5">
                {artSeo.items.map((it) => (
                  <li key={it.key} data-testid={`art-seo-${it.key}`} className="flex items-start gap-2 text-xs">
                    {it.ok
                      ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                      : <AlertTriangle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${it.warn ? "text-amber-400" : "text-red-400"}`} />}
                    <span>
                      <span className={it.ok ? "text-foreground" : "text-muted-foreground"}>{it.label}</span>
                      {!it.ok && <span className="text-muted-foreground"> — {it.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {artForm.status === "published" && artSeo.score < 50 && (
                <p className="mt-2 text-xs text-amber-300">Skor masih rendah — pertimbangkan melengkapi sebelum menerbitkan.</p>
              )}
            </div>
          </div>
          <DialogFooter><Button data-testid="art-submit" onClick={saveArticle} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan Artikel</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WordPress import */}
      <Dialog open={wpDialog} onOpenChange={setWpDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="wp-import-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Import Artikel dari WordPress</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">Masukkan alamat situs WordPress. Sistem mengambil artikel terbaru via REST API (<span className="font-mono-terminal">/wp-json</span>) beserta judul, isi, ringkasan, dan gambar utama.</p>
            <div className="grid gap-1.5"><Label>URL Situs WordPress</Label><Input data-testid="wp-url" value={wpForm.site_url} onChange={(e) => setWpForm({ ...wpForm, site_url: e.target.value })} placeholder="https://contohblog.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Jumlah Artikel</Label><Input data-testid="wp-count" type="number" min={1} max={50} value={wpForm.count} onChange={(e) => setWpForm({ ...wpForm, count: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Status Impor</Label>
                <Select value={wpForm.status} onValueChange={(v) => setWpForm({ ...wpForm, status: v })}>
                  <SelectTrigger data-testid="wp-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft (tinjau dulu)</SelectItem><SelectItem value="published">Langsung Terbit</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {wpResult && (
              <div className="border border-border bg-card p-3 text-sm" data-testid="wp-result">
                <p><span className="font-bold text-primary">{wpResult.imported}</span> diimpor · <span className="font-bold">{wpResult.skipped}</span> dilewati (duplikat) · {wpResult.total_fetched} diambil</p>
                {wpResult.items?.length > 0 && (
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                    {wpResult.items.map((it) => <li key={it.slug}>• {it.title}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="wp-import-submit" onClick={importWordpress} disabled={wpLoading} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
              {wpLoading ? "Mengimpor..." : "Impor Sekarang"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WordPress XML (WXR) file import */}
      <Dialog open={xmlDialog} onOpenChange={(o) => { if (xmlPhase !== "uploading" && xmlPhase !== "processing") setXmlDialog(o); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="xml-import-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Import Artikel dari File XML WordPress</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">Unggah file ekspor WordPress (<span className="font-mono-terminal">.xml</span> / WXR) dari menu <span className="font-semibold">Tools → Export</span>. Sistem membaca judul, isi, ringkasan, kategori, meta SEO (Yoast), dan gambar utama secara otomatis.</p>
            <div className="grid gap-1.5">
              <Label>File XML WordPress</Label>
              <Input data-testid="xml-file" type="file" accept=".xml,text/xml,application/xml" onChange={(e) => setXmlFile(e.target.files?.[0] || null)} />
              {xmlFile && <span className="text-xs text-muted-foreground">{xmlFile.name} · {(xmlFile.size / 1024 / 1024).toFixed(1)} MB</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Status Impor</Label>
                <Select value={xmlStatus} onValueChange={setXmlStatus}>
                  <SelectTrigger data-testid="xml-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft (tinjau dulu)</SelectItem><SelectItem value="published">Langsung Terbit</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Tipe Post (opsional)</Label><Input data-testid="xml-type" value={xmlType} onChange={(e) => setXmlType(e.target.value)} placeholder="otomatis (mis. post)" /></div>
            </div>
            {(xmlPhase === "uploading" || xmlProgress > 0) && (
              <div>
                <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>Mengunggah file...</span><span>{xmlProgress}%</span></div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-border"><div className="h-full bg-primary transition-all" style={{ width: `${xmlProgress}%` }} /></div>
              </div>
            )}
            {xmlPhase === "processing" && (
              <div className="border border-border bg-card p-3 text-sm" data-testid="xml-processing">
                <p className="animate-pulse">Memproses di latar belakang{xmlResult?.post_type ? ` (tipe: ${xmlResult.post_type})` : ""}... {xmlResult?.total ? `${xmlResult.imported}/${xmlResult.total} artikel` : ""}</p>
              </div>
            )}
            {xmlPhase === "done" && xmlResult && (
              <div className="border border-border bg-card p-3 text-sm" data-testid="xml-result">
                {xmlResult.error
                  ? <p className="text-red-400">Gagal: {xmlResult.error}</p>
                  : <p><span className="font-bold text-primary">{xmlResult.imported}</span> artikel diimpor · <span className="font-bold">{xmlResult.skipped}</span> dilewati (duplikat) · tipe <span className="font-mono-terminal">{xmlResult.post_type}</span></p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="xml-import-submit" onClick={importXml} disabled={!xmlFile || xmlPhase === "uploading" || xmlPhase === "processing"} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
              {xmlPhase === "uploading" ? "Mengunggah..." : xmlPhase === "processing" ? "Memproses..." : "Impor Sekarang"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
